import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import { estimateStripeFeeMinor, getPlatformFeeMinor, getStripeFeeConfig, grossUpForStripeFeeMinor } from '../../../../utils/fees';
import { getFirestoreAssertionRecoveryMessage } from '../../../../utils/firestoreAssertionRecovery';
import ErrorMessage from '../../formComponents/ErrorMessage';
import SubmitButton from '../../formComponents/SubmitButton';
import useStripePaymentGuard from '../../../payments/useStripePaymentGuard';
import {
  calculateLearnToSailConversionStartsAt,
  buildLearnToSailEventDocId,
  buildLearnToSailEventPayload,
  calculateLearnToSailCheckoutPreview,
  calculateLearnToSailFinalSessionEnd,
  calculateLearnToSailPurgeAt,
  createLearnToSailSession,
  filterEditableTeamTags,
  getLearnToSailCourseFeeSettings,
  formatLearnToSailDateInputValue,
  getLearnToSailGeneralSettings,
  getLearnToSailCourseTagName,
  isLearnToSailSystemTag,
  isLearnToSailCoursePubliclyActionable,
  normalizeLearnToSailConversionStartMode,
  normalizeLearnToSailSignupMode,
  normalizeLearnToSailSessions,
  upsertLearnToSailSystemTags,
} from '../../../teams/utils/learnToSail';
import { getRestrictableDashboardCards, normalizeRestrictedCardIds } from '../../../teams/utils/moduleRestrictions';
import { isCoursePaymentSetupComplete } from '../../../join/courseSignupSetup';
import CourseBoatReservations from '../courses/CourseBoatReservations';

const saveCourseAssets = httpsCallable(functions, 'saveCourseAssetPlan');
const releaseCourseAssets = httpsCallable(functions, 'releaseCourseAssetPlan');

const JOIN_FORM_QUESTION_TYPES = ['text', 'textarea', 'number', 'yesno', 'date', 'select', 'checkbox'];
const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];
const UNSAVED_GENERAL_SETUP_MESSAGE = 'You have unsaved Learn to Sail general setup changes. Leave without saving them?';

function TogglePill() {
  return (
    <span className="ltr-card-restriction__switch" aria-hidden="true">
      <span className="ltr-card-restriction__switch-thumb" />
    </span>
  );
}

function normalizeGeneralSetupSnapshot(settings = {}, courseDefaults = {}) {
  const normalizedSettings = {
    waitlistOpen: settings.waitlistOpen === true,
    memberNestFeeHandling: settings.memberNestFeeHandling === 'deduct' ? 'deduct' : 'add',
    stripeFeeHandling: settings.stripeFeeHandling === 'deduct' ? 'deduct' : 'add',
    restrictActiveCourseCards: settings.restrictActiveCourseCards === true,
    activeCourseVisibleCardIds: normalizeRestrictedCardIds(settings.activeCourseVisibleCardIds || []).slice().sort(),
  };
  const normalizedDefaults = normalizeCourseDefaults(courseDefaults);
  return JSON.stringify({
    settings: normalizedSettings,
    courseDefaults: {
      ...normalizedDefaults,
      courseFeeMajor: String(normalizedDefaults.courseFeeMajor || ''),
    },
  });
}

function isPricingQuestion(question = {}) {
  return PRICING_QUESTION_IDS.includes(question?.id);
}

function normalizeQuestionOptions(options = []) {
  return Array.isArray(options)
    ? options.map((option) => String(option || '').trim()).filter(Boolean)
    : [];
}

function createLearnToSailJoinQuestion(input = {}) {
  const type = JOIN_FORM_QUESTION_TYPES.includes(input?.type) ? input.type : 'text';
  const id = typeof input?.id === 'string' && input.id.trim()
    ? input.id.trim()
    : (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `lts_question_${Date.now()}`);
  return {
    id,
    label: typeof input?.label === 'string' ? input.label : '',
    type,
    required: type === 'checkbox' ? true : input?.required === true,
    syncToProfile: input?.syncToProfile === true,
    options: type === 'select' ? normalizeQuestionOptions(input?.options) : [],
  };
}

function normalizeLearnToSailJoinQuestions(questions = []) {
  return (Array.isArray(questions) ? questions : [])
    .filter((question) => !isPricingQuestion(question))
    .map((question) => createLearnToSailJoinQuestion(question))
    .filter((question) => question.id);
}

function emptyJoinSetup() {
  return {
    reviewMode: 'approval_required',
    questionMode: 'club_join_form',
    indicativePriceMajor: '',
    indicativePriceNote: '',
    questions: [],
  };
}

function normalizeJoinSetup(config = {}) {
  const mode = typeof config?.questionMode === 'string' ? config.questionMode.trim() : '';
  const reviewMode = typeof config?.reviewMode === 'string' ? config.reviewMode.trim() : '';
  return {
    reviewMode: reviewMode === 'auto_waitlist' ? 'auto_waitlist' : 'approval_required',
    questionMode: ['club_join_form', 'club_join_form_plus_l2s', 'l2s_only'].includes(mode)
      ? mode
      : 'club_join_form',
    indicativePriceMajor: typeof config?.indicativePriceMajor === 'string'
      ? config.indicativePriceMajor
      : (Number.isFinite(Number(config?.indicativePriceMinor)) && Number(config?.indicativePriceMinor) > 0
        ? String((Number(config.indicativePriceMinor) / 100).toFixed(2))
        : ''),
    indicativePriceNote: typeof config?.indicativePriceNote === 'string' ? config.indicativePriceNote : '',
    questions: normalizeLearnToSailJoinQuestions(config?.questions || []),
  };
}

function emptyCourseDefaults() {
  return {
    description: '',
    signupMode: 'closed',
    requireSessionRsvp: true,
    capacity: 12,
    courseFeeMajor: '',
    postCoursePurgeDays: 7,
    conversionStartMode: 'after_course_end',
    membershipPricingMode: 'full_price',
    membershipSelectionMode: 'member_picks',
    fixedMembershipPricingTag: '',
    locationId: '',
    locationName: '',
    locationStreet: '',
    locationCity: '',
    locationPostcode: '',
    locationCountry: '',
  };
}

function normalizeCourseDefaults(config = {}) {
  return {
    description: typeof config?.description === 'string' ? config.description : '',
    signupMode: normalizeLearnToSailSignupMode(config),
    requireSessionRsvp: config?.requireSessionRsvp !== false,
    capacity: Math.max(1, Number(config?.capacity || 12)),
    courseFeeMajor: typeof config?.courseFeeMajor === 'string'
      ? config.courseFeeMajor
      : (Number.isFinite(Number(config?.courseFeeMinor)) && Number(config?.courseFeeMinor) > 0
        ? String((Number(config.courseFeeMinor) / 100).toFixed(2))
        : ''),
    postCoursePurgeDays: Math.max(1, Number(config?.postCoursePurgeDays || 7)),
    conversionStartMode: normalizeLearnToSailConversionStartMode(config?.conversionStartMode),
    membershipPricingMode: config?.membershipPricingMode === 'credit_course_fee' ? 'credit_course_fee' : 'full_price',
    membershipSelectionMode: config?.membershipSelectionMode === 'fixed_pricing_tag' ? 'fixed_pricing_tag' : 'member_picks',
    fixedMembershipPricingTag: typeof config?.fixedMembershipPricingTag === 'string' ? config.fixedMembershipPricingTag : '',
    locationId: typeof config?.locationId === 'string' ? config.locationId : '',
    locationName: typeof config?.locationName === 'string' ? config.locationName : '',
    locationStreet: typeof config?.locationStreet === 'string' ? config.locationStreet : '',
    locationCity: typeof config?.locationCity === 'string' ? config.locationCity : '',
    locationPostcode: typeof config?.locationPostcode === 'string' ? config.locationPostcode : '',
    locationCountry: typeof config?.locationCountry === 'string' ? config.locationCountry : '',
  };
}

function emptyCourse(courseDefaults = {}, feeDefaults = {}) {
  const normalizedDefaults = {
    ...emptyCourseDefaults(),
    ...normalizeCourseDefaults(courseDefaults),
  };
  const normalizedFeeDefaults = {
    memberNestFeeHandling: feeDefaults?.memberNestFeeHandling === 'deduct' ? 'deduct' : 'add',
    stripeFeeHandling: feeDefaults?.stripeFeeHandling === 'deduct' ? 'deduct' : 'add',
  };
  return {
    id: null,
    name: '',
    status: 'active',
    ...normalizedDefaults,
    ...normalizedFeeDefaults,
    manualConversionStartAt: '',
    sessions: [createLearnToSailSession()],
  };
}

function normalizeCourseFromDoc(course = {}, feeDefaults = {}) {
  const rawFeeMajor = typeof course?.courseFeeMajor === 'string' ? course.courseFeeMajor.trim() : '';
  const feeMinor = Number.isFinite(Number(course?.courseFeeMinor))
    ? Math.max(0, Math.round(Number(course.courseFeeMinor || 0)))
    : (rawFeeMajor && Number.isFinite(Number(rawFeeMajor))
      ? Math.max(0, Math.round(Number(rawFeeMajor) * 100))
      : 0);
  const feeSettings = getLearnToSailCourseFeeSettings({ ...course, courseFeeMinor: feeMinor }, feeDefaults);
  return {
    id: course?.id || null,
    name: typeof course?.name === 'string' ? course.name : '',
    status: course?.status === 'cancelled' ? 'cancelled' : 'active',
    description: typeof course?.description === 'string' ? course.description : '',
    signupMode: normalizeLearnToSailSignupMode(course),
    requireSessionRsvp: course?.requireSessionRsvp !== false,
    capacity: Math.max(1, Number(course?.capacity || 1)),
    courseFeeMinor: feeMinor,
    courseFeeMajor: rawFeeMajor || (feeMinor > 0 ? String((feeMinor / 100).toFixed(2)) : ''),
    memberNestFeeHandling: feeSettings.memberNestFeeHandling,
    stripeFeeHandling: feeSettings.stripeFeeHandling,
    postCoursePurgeDays: Math.max(1, Number(course?.postCoursePurgeDays || 7)),
    conversionStartMode: normalizeLearnToSailConversionStartMode(course?.conversionStartMode),
    manualConversionStartAt: formatLearnToSailDateInputValue(course?.manualConversionStartAt || ''),
    membershipPricingMode: course?.membershipPricingMode === 'credit_course_fee' ? 'credit_course_fee' : 'full_price',
    membershipSelectionMode: course?.membershipSelectionMode === 'fixed_pricing_tag' ? 'fixed_pricing_tag' : 'member_picks',
    fixedMembershipPricingTag: typeof course?.fixedMembershipPricingTag === 'string' ? course.fixedMembershipPricingTag : '',
    locationId: typeof course?.locationId === 'string' ? course.locationId : '',
    locationName: typeof course?.locationName === 'string' ? course.locationName : '',
    locationStreet: typeof course?.locationStreet === 'string' ? course.locationStreet : '',
    locationCity: typeof course?.locationCity === 'string' ? course.locationCity : '',
    locationPostcode: typeof course?.locationPostcode === 'string' ? course.locationPostcode : '',
    locationCountry: typeof course?.locationCountry === 'string' ? course.locationCountry : '',
    sessions: normalizeLearnToSailSessions(course?.sessions || []).length > 0
      ? normalizeLearnToSailSessions(course?.sessions || [])
      : [createLearnToSailSession()],
  };
}

function formatDateTime(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return 'Not set';
  return value.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function toMillis(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
}

function participantCountSummary(participants = []) {
  return (Array.isArray(participants) ? participants : []).reduce((acc, participant) => {
    const status = String(participant?.status || '').trim().toLowerCase();
    if (status) acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
}

function formatPersonName(profile = {}, fallback = 'Unknown member') {
  const first = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
  const last = typeof profile?.lastName === 'string' ? profile.lastName.trim() : '';
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const display = typeof profile?.displayName === 'string' ? profile.displayName.trim() : '';
  if (display) return display;
  const username = typeof profile?.username === 'string' ? profile.username.trim() : '';
  if (username) return username;
  return fallback;
}

function formatWaitlistAnswerValue(response = {}) {
  const value = response?.value;
  if (value === null || value === undefined || value === '') return 'No answer';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function formatTimestampLabel(value, fallback = 'Not recorded') {
  if (!value) return fallback;
  if (typeof value?.toDate === 'function') return formatDateTime(value.toDate());
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : formatDateTime(parsed);
}

function formatParticipantStatus(status = '') {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'Unknown';
  if (normalized === 'released_waitlist') return 'Released to payment';
  if (normalized === 'active_course') return 'Active on course';
  if (normalized === 'awaiting_conversion') return 'Awaiting conversion';
  return normalized
    .split('_')
    .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : ''))
    .join(' ');
}

function formatCourseAccessLabel(course = {}, waitlistOpen = false) {
  const signupMode = normalizeLearnToSailSignupMode(course);
  if (signupMode === 'open_to_all') return 'Public first come';
  if (signupMode === 'waitlist_only') {
    return waitlistOpen ? 'Admin release via waitlist' : 'Needs setup: open waitlist';
  }
  return 'Closed';
}

function resolveRsvpMemberId(docSnap) {
  const data = docSnap?.data?.() || {};
  if (typeof data?.memberId === 'string' && data.memberId.trim()) return data.memberId.trim();
  if (typeof data?.userId === 'string' && data.userId.trim()) return data.userId.trim();
  return typeof docSnap?.id === 'string' ? docSnap.id.trim().split('__')[0] : '';
}

function formatRsvpStatusLabel(status = '') {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'yes') return 'Yes';
  if (normalized === 'no') return 'No';
  return 'No response';
}

function formatMoney(minor = 0, currency = 'GBP') {
  const amount = Number(minor || 0) / 100;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: String(currency || 'GBP').toUpperCase(),
  }).format(amount);
}

function getLearnToSailErrorMessage(error, fallback) {
  return getFirestoreAssertionRecoveryMessage(error, fallback);
}

function comparePendingApprovalEntries(a = {}, b = {}) {
  const dateDelta = toMillis(a?.createdAt) - toMillis(b?.createdAt);
  if (dateDelta !== 0) return dateDelta;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

function compareApprovedWaitlistEntries(a = {}, b = {}) {
  const aPosition = Number.isFinite(Number(a?.queuePosition)) ? Number(a.queuePosition) : Number.POSITIVE_INFINITY;
  const bPosition = Number.isFinite(Number(b?.queuePosition)) ? Number(b.queuePosition) : Number.POSITIVE_INFINITY;
  if (aPosition !== bPosition) return aPosition - bPosition;
  const approvedDelta = toMillis(a?.approvedAt) - toMillis(b?.approvedAt);
  if (approvedDelta !== 0) return approvedDelta;
  const createdDelta = toMillis(a?.createdAt) - toMillis(b?.createdAt);
  if (createdDelta !== 0) return createdDelta;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

async function migrateTagNameAcrossCollection(collectionRef, oldTagName, newTagName) {
  if (!oldTagName || !newTagName || oldTagName === newTagName) return;
  const snapshot = await getDocs(collectionRef);
  const docsToUpdate = snapshot.docs.filter((docSnap) => Boolean(docSnap.data()?.tags?.[oldTagName]));
  for (let index = 0; index < docsToUpdate.length; index += 450) {
    const batch = writeBatch(db);
    docsToUpdate.slice(index, index + 450).forEach((docSnap) => {
      const currentTags = { ...(docSnap.data()?.tags || {}) };
      delete currentTags[oldTagName];
      currentTags[newTagName] = true;
      batch.set(docSnap.ref, { tags: currentTags }, { merge: true });
    });
    await batch.commit();
  }
}

async function stripTagNameAcrossCollection(collectionRef, tagName) {
  if (!tagName) return;
  const snapshot = await getDocs(collectionRef);
  const docsToUpdate = snapshot.docs.filter((docSnap) => Boolean(docSnap.data()?.tags?.[tagName]));
  for (let index = 0; index < docsToUpdate.length; index += 450) {
    const batch = writeBatch(db);
    docsToUpdate.slice(index, index + 450).forEach((docSnap) => {
      const currentTags = { ...(docSnap.data()?.tags || {}) };
      delete currentTags[tagName];
      batch.set(docSnap.ref, { tags: currentTags }, { merge: true });
    });
    await batch.commit();
  }
}

export default function EditLearnToSailForm({
  isOpen,
  onClose,
  onUnsavedGeneralChangesChange,
  teamId,
  team,
  focusCourseId = null,
  createNew = false,
  initialPanel = 'courses',
}) {
  const restrictableDashboardCards = useMemo(
    () => getRestrictableDashboardCards(team?.teamMetadata || {}),
    [team?.teamMetadata]
  );
  const restrictableDashboardCardIds = useMemo(
    () => restrictableDashboardCards.map((card) => card.id),
    [restrictableDashboardCards]
  );
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(teamId);
  const [activePanel, setActivePanel] = useState('courses');
  const [courseListFilter, setCourseListFilter] = useState('upcoming');
  const [courses, setCourses] = useState([]);
  const [activeCourseId, setActiveCourseId] = useState('__new__');
  const [draft, setDraft] = useState(emptyCourse());
  const [participants, setParticipants] = useState([]);
  const [participantProfiles, setParticipantProfiles] = useState({});
  const [participantRsvpBySession, setParticipantRsvpBySession] = useState({});
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistProfiles, setWaitlistProfiles] = useState({});
  const [expandedWaitlistEntries, setExpandedWaitlistEntries] = useState({});
  const [locations, setLocations] = useState([]);
  const [clubJoinQuestions, setClubJoinQuestions] = useState([]);
  const [joinSetup, setJoinSetup] = useState(emptyJoinSetup());
  const [courseDefaults, setCourseDefaults] = useState(emptyCourseDefaults());
  const [stripeSetup, setStripeSetup] = useState({});
  const [copiedLinkKey, setCopiedLinkKey] = useState('');
  const [teamWaitlistOpen, setTeamWaitlistOpen] = useState(Boolean(team?.modules?.sailing?.learnToSail?.waitlistOpen));
  const [memberNestFeeHandling, setMemberNestFeeHandling] = useState(getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {}).memberNestFeeHandling);
  const [stripeFeeHandling, setStripeFeeHandling] = useState(getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {}).stripeFeeHandling);
  const [restrictActiveCourseCards, setRestrictActiveCourseCards] = useState(getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {}).restrictActiveCourseCards);
  const [activeCourseVisibleCardIds, setActiveCourseVisibleCardIds] = useState(getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {}).activeCourseVisibleCardIds);
  const [savedGeneralSettings, setSavedGeneralSettings] = useState(getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {}));
  const [savedCourseDefaults, setSavedCourseDefaults] = useState(emptyCourseDefaults());
  const [bulkFillCount, setBulkFillCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const editablePricingTags = useMemo(
    () => filterEditableTeamTags(team?.tags || []).filter((tag) => tag?.name),
    [team?.tags]
  );

  useEffect(() => {
    const settings = getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {});
    setTeamWaitlistOpen(settings.waitlistOpen);
    setMemberNestFeeHandling(settings.memberNestFeeHandling);
    setStripeFeeHandling(settings.stripeFeeHandling);
    setRestrictActiveCourseCards(settings.restrictActiveCourseCards);
    setActiveCourseVisibleCardIds(settings.activeCourseVisibleCardIds);
    setSavedGeneralSettings(settings);
  }, [team?.modules?.sailing?.learnToSail]);

  useEffect(() => {
    if (!isOpen) return;
    setActivePanel(['general', 'courses', 'waitlist', 'joinSetup'].includes(initialPanel) ? initialPanel : 'courses');
    setCopiedLinkKey('');
  }, [initialPanel, isOpen]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [coursesSnap, joinFormSnap, learnToSailJoinSnap, learnToSailConfigSnap, stripeSetupSnap] = await Promise.all([
          getDocs(query(collection(db, 'teams', teamId, 'learnToSailCourses'), orderBy('updatedAt', 'desc'))),
          getDoc(doc(db, 'teams', teamId, 'joinFormConfig', 'config')).catch(() => null),
          getDoc(doc(db, 'teams', teamId, 'learnToSailJoinConfig', 'config')).catch(() => null),
          getDoc(doc(db, 'teams', teamId, 'learnToSailConfig', 'config')).catch(() => null),
          getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')).catch(() => null),
        ]);
        if (cancelled) return;
        const courseFeeDefaults = getLearnToSailGeneralSettings(team?.modules?.sailing?.learnToSail || {});
        const nextCourses = coursesSnap.docs.map((docSnap) => normalizeCourseFromDoc({ id: docSnap.id, ...docSnap.data() }, courseFeeDefaults));
        const nextCourseDefaults = normalizeCourseDefaults(
          learnToSailConfigSnap?.exists() ? (learnToSailConfigSnap.data()?.courseDefaults || {}) : {}
        );
        setCourses(nextCourses);
        setCourseDefaults(nextCourseDefaults);
        setSavedCourseDefaults(nextCourseDefaults);
        setClubJoinQuestions(
          normalizeLearnToSailJoinQuestions(joinFormSnap?.exists() ? (joinFormSnap.data()?.questions || []) : [])
        );
        setJoinSetup(normalizeJoinSetup(learnToSailJoinSnap?.exists() ? (learnToSailJoinSnap.data() || {}) : {}));
        setStripeSetup(stripeSetupSnap?.exists() ? (stripeSetupSnap.data() || {}) : {});
        if (createNew) {
          setActiveCourseId('__new__');
          setDraft(emptyCourse(nextCourseDefaults, courseFeeDefaults));
          return;
        }
        const nextActiveId = focusCourseId && nextCourses.some((course) => course.id === focusCourseId)
          ? focusCourseId
          : nextCourses[0]?.id || '__new__';
        setActiveCourseId(nextActiveId);
        setDraft(nextActiveId === '__new__'
          ? emptyCourse(nextCourseDefaults, courseFeeDefaults)
          : normalizeCourseFromDoc(nextCourses.find((course) => course.id === nextActiveId) || emptyCourse(nextCourseDefaults, courseFeeDefaults), courseFeeDefaults));
      } catch (err) {
        if (!cancelled) setError(getLearnToSailErrorMessage(err, 'Failed to load Learn to Sail courses.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [createNew, focusCourseId, isOpen, team?.modules?.sailing?.learnToSail, teamId]);

  useEffect(() => {
    if (!isOpen || !teamId) {
      setWaitlistEntries([]);
      setWaitlistProfiles({});
      return;
    }
    let cancelled = false;
    const loadWaitlist = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'teams', teamId, 'learnToSailWaitlist'), orderBy('createdAt', 'asc')));
        if (cancelled) return;
        setWaitlistEntries(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch {
        if (!cancelled) setWaitlistEntries([]);
      }
    };
    loadWaitlist();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || waitlistEntries.length === 0) {
      setWaitlistProfiles({});
      return;
    }
    let cancelled = false;
    const loadWaitlistProfiles = async () => {
      try {
        const uniqueUserIds = Array.from(new Set(
          waitlistEntries.flatMap((entry) => {
            const ids = [entry?.id, entry?.userId, entry?.payerUserId];
            return ids
              .map((value) => (typeof value === 'string' ? value.trim() : ''))
              .filter(Boolean);
          })
        ));
        const snapshots = await Promise.all(
          uniqueUserIds.map(async (uid) => {
            const snap = await getDoc(doc(db, 'users', uid)).catch(() => null);
            return [uid, snap?.exists() ? (snap.data() || {}) : null];
          })
        );
        if (!cancelled) {
          setWaitlistProfiles(Object.fromEntries(snapshots));
        }
      } catch {
        if (!cancelled) setWaitlistProfiles({});
      }
    };
    loadWaitlistProfiles();
    return () => {
      cancelled = true;
    };
  }, [isOpen, waitlistEntries]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    let cancelled = false;
    const loadLocations = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'teams', teamId, 'locations'), orderBy('name', 'asc')));
        if (cancelled) return;
        setLocations(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch {
        if (!cancelled) setLocations([]);
      }
    };
    loadLocations();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!teamId || !activeCourseId || activeCourseId === '__new__') {
      setParticipants([]);
      setParticipantProfiles({});
      return;
    }
    let cancelled = false;
    const loadParticipants = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'teams', teamId, 'learnToSailCourses', activeCourseId, 'participants'));
        if (cancelled) return;
        setParticipants(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch {
        if (!cancelled) setParticipants([]);
      }
    };
    loadParticipants();
    return () => {
      cancelled = true;
    };
  }, [activeCourseId, teamId]);

  useEffect(() => {
    if (!isOpen || participants.length === 0) {
      setParticipantProfiles({});
      return;
    }
    let cancelled = false;
    const loadParticipantProfiles = async () => {
      try {
        const uniqueUserIds = Array.from(new Set(
          participants.flatMap((participant) => {
            const ids = [participant?.id, participant?.userId, participant?.payerUserId];
            return ids
              .map((value) => (typeof value === 'string' ? value.trim() : ''))
              .filter(Boolean);
          })
        ));
        const snapshots = await Promise.all(
          uniqueUserIds.map(async (uid) => {
            const snap = await getDoc(doc(db, 'users', uid)).catch(() => null);
            return [uid, snap?.exists() ? (snap.data() || {}) : null];
          })
        );
        if (!cancelled) {
          setParticipantProfiles(Object.fromEntries(snapshots));
        }
      } catch {
        if (!cancelled) setParticipantProfiles({});
      }
    };
    loadParticipantProfiles();
    return () => {
      cancelled = true;
    };
  }, [isOpen, participants]);

  useEffect(() => {
    if (!isOpen || !teamId || activeCourseId === '__new__') {
      setParticipantRsvpBySession({});
      return;
    }
    const normalizedSessions = normalizeLearnToSailSessions(draft.sessions || []);
    if (normalizedSessions.length === 0) {
      setParticipantRsvpBySession({});
      return;
    }
    let cancelled = false;
    const loadSessionRsvps = async () => {
      try {
        const nextEntries = await Promise.all(
          normalizedSessions.map(async (session) => {
            const eventId = session.eventId || buildLearnToSailEventDocId(activeCourseId, session.id);
            if (!eventId) return [session.id, {}];
            const snapshot = await getDocs(collection(db, 'teams', teamId, 'events', eventId, 'rsvps')).catch(() => null);
            const responseMap = {};
            (snapshot?.docs || []).forEach((docSnap) => {
              const memberId = resolveRsvpMemberId(docSnap);
              const status = String(docSnap.data()?.status || '').trim().toLowerCase();
              if (!memberId || (status !== 'yes' && status !== 'no')) return;
              responseMap[memberId] = status;
            });
            return [session.id, responseMap];
          })
        );
        if (!cancelled) setParticipantRsvpBySession(Object.fromEntries(nextEntries));
      } catch {
        if (!cancelled) setParticipantRsvpBySession({});
      }
    };
    loadSessionRsvps();
    return () => {
      cancelled = true;
    };
  }, [activeCourseId, draft.sessions, isOpen, teamId]);

  const participantSummary = useMemo(() => participantCountSummary(participants), [participants]);
  const normalizedSessions = useMemo(() => normalizeLearnToSailSessions(draft.sessions || []), [draft.sessions]);
  const finalSessionEnd = useMemo(() => calculateLearnToSailFinalSessionEnd(normalizedSessions), [normalizedSessions]);
  const conversionStartsAt = useMemo(
    () => calculateLearnToSailConversionStartsAt(normalizedSessions, {
      conversionStartMode: draft.conversionStartMode,
      manualConversionStartAt: draft.manualConversionStartAt,
    }),
    [draft.conversionStartMode, draft.manualConversionStartAt, normalizedSessions]
  );
  const purgeAt = useMemo(
    () => calculateLearnToSailPurgeAt(normalizedSessions, draft.postCoursePurgeDays, {
      conversionStartMode: draft.conversionStartMode,
      manualConversionStartAt: draft.manualConversionStartAt,
    }),
    [draft.conversionStartMode, draft.manualConversionStartAt, draft.postCoursePurgeDays, normalizedSessions]
  );
  const generalFeePreview = useMemo(() => {
    const currency = String(stripeSetup?.currency || 'GBP').toUpperCase();
    const courseFeeMinor = Math.max(0, Math.round(Number(courseDefaults.courseFeeMajor || 0) * 100));
    const configuredPlatformFeeMinor = Math.max(0, getPlatformFeeMinor({
      currency,
      teamFeeCfg: stripeSetup || {},
    }));
    const stripeCfg = getStripeFeeConfig({
      currency,
      teamFeeCfg: stripeSetup || {},
    });
    const chargedPlatformFeeMinor = memberNestFeeHandling === 'add' ? configuredPlatformFeeMinor : 0;
    const subtotalBeforeStripe = courseFeeMinor + chargedPlatformFeeMinor;
    const chargedTotalMinor = stripeFeeHandling === 'add'
      ? grossUpForStripeFeeMinor(subtotalBeforeStripe, stripeCfg)
      : subtotalBeforeStripe;
    const actualStripeFeeMinor = stripeFeeHandling === 'add'
      ? Math.max(0, chargedTotalMinor - subtotalBeforeStripe)
      : Math.max(0, estimateStripeFeeMinor(chargedTotalMinor, stripeCfg));
    const clubKeepsMinor = Math.max(0, chargedTotalMinor - configuredPlatformFeeMinor - actualStripeFeeMinor);
    return {
      currency,
      courseFeeMinor,
      platformFeeMinor: configuredPlatformFeeMinor,
      chargedPlatformFeeMinor,
      stripeFeeMinor: actualStripeFeeMinor,
      chargedStripeFeeMinor: stripeFeeHandling === 'add' ? actualStripeFeeMinor : 0,
      chargedTotalMinor,
      clubKeepsMinor,
      conversionCreditMinor: Math.max(0, Math.min(courseFeeMinor, clubKeepsMinor)),
    };
  }, [courseDefaults.courseFeeMajor, memberNestFeeHandling, stripeFeeHandling, stripeSetup]);
  const courseFeePreview = useMemo(() => {
    const courseFeeMinor = Math.max(0, Math.round(Number(draft.courseFeeMajor || 0) * 100));
    return calculateLearnToSailCheckoutPreview({
      courseFeeMinor,
      currency: stripeSetup?.currency || 'GBP',
      teamFeeCfg: stripeSetup || {},
      learnToSailModule: team?.modules?.sailing?.learnToSail || {},
      course: {
        ...draft,
        courseFeeMinor,
      },
    });
  }, [draft, stripeSetup, team?.modules?.sailing?.learnToSail]);
  const effectiveJoinQuestions = useMemo(() => {
    if (joinSetup.questionMode === 'l2s_only') return joinSetup.questions || [];
    if (joinSetup.questionMode === 'club_join_form_plus_l2s') {
      const byId = new Map();
      [...(clubJoinQuestions || []), ...(joinSetup.questions || [])].forEach((question) => {
        if (!question?.id) return;
        byId.set(question.id, question);
      });
      return Array.from(byId.values());
    }
    return clubJoinQuestions || [];
  }, [clubJoinQuestions, joinSetup.questionMode, joinSetup.questions]);
  const selectedSavedCourse = useMemo(
    () => courses.find((course) => course.id === activeCourseId) || null,
    [activeCourseId, courses]
  );
  const hasPublicCourse = useMemo(
    () => courses.some((course) => normalizeLearnToSailSignupMode(course) === 'open_to_all'),
    [courses]
  );
  const genericJoinUrl = useMemo(() => (
    teamId && typeof window !== 'undefined' && (teamWaitlistOpen || hasPublicCourse)
      ? `${window.location.origin}/join/${teamId}?joinMode=learn-to-sail`
      : ''
  ), [hasPublicCourse, teamId, teamWaitlistOpen]);
  const selectedCourseLinkAvailable = Boolean(
    selectedSavedCourse
    && isLearnToSailCoursePubliclyActionable(selectedSavedCourse, teamWaitlistOpen)
  );
  const selectedCourseJoinUrl = useMemo(() => (
    activeCourseId && activeCourseId !== '__new__' && selectedCourseLinkAvailable && typeof window !== 'undefined'
      ? `${window.location.origin}/join/${teamId}?joinMode=learn-to-sail&courseId=${activeCourseId}`
      : ''
  ), [activeCourseId, selectedCourseLinkAvailable, teamId]);
  const selectedCourseLinkUnavailableMessage = useMemo(() => {
    if (!selectedSavedCourse) return 'Save a course before copying a selected course link.';
    const signupMode = normalizeLearnToSailSignupMode(selectedSavedCourse);
    if (signupMode === 'waitlist_only' && !teamWaitlistOpen) {
      return 'Open the generic Learn to Sail waitlist before sharing this admin-release course link.';
    }
    if (signupMode === 'closed') {
      return 'Change this course to Public first come, or open the generic waitlist and use Admin release, before sharing a course link.';
    }
    return '';
  }, [selectedSavedCourse, teamWaitlistOpen]);
  const pendingApprovalEntries = useMemo(
    () => waitlistEntries
      .filter((entry) => String(entry?.status || '').trim().toLowerCase() === 'awaiting_approval')
      .sort(comparePendingApprovalEntries),
    [waitlistEntries]
  );
  const approvedWaitlistEntries = useMemo(
    () => waitlistEntries
      .filter((entry) => String(entry?.status || '').trim().toLowerCase() === 'waitlisted')
      .sort(compareApprovedWaitlistEntries),
    [waitlistEntries]
  );
  const currentCourseReservedCount = useMemo(
    () => participants.filter((participant) => ['released_waitlist', 'active_course'].includes(String(participant?.status || '').trim().toLowerCase())).length,
    [participants]
  );
  const currentCourseCapacity = Math.max(0, Number(draft.capacity || 0));
  const currentCourseAvailableSlots = Math.max(0, currentCourseCapacity - currentCourseReservedCount);
  const filteredCourses = useMemo(() => {
    const now = Date.now();
    return courses.filter((course) => {
      if (courseListFilter !== 'past' && courseListFilter !== 'upcoming') return true;
      const finalEnd = calculateLearnToSailFinalSessionEnd(normalizeLearnToSailSessions(course.sessions || []));
      const finalMillis = finalEnd instanceof Date && !Number.isNaN(finalEnd.getTime())
        ? finalEnd.getTime()
        : Number.POSITIVE_INFINITY;
      return courseListFilter === 'past' ? finalMillis < now : finalMillis >= now;
    });
  }, [courseListFilter, courses]);

  const getWaitlistEntryName = (entry) => {
    const userId = typeof entry?.id === 'string' ? entry.id.trim() : '';
    const profile = waitlistProfiles?.[userId] || null;
    const formNameValue = entry?.formResponses?.find((response) => response?.id === 'fullName')?.value;
    const formName = typeof formNameValue === 'string' ? formNameValue.trim() : '';
    return formatPersonName(
      profile || {},
      formName || 'Unknown member'
    );
  };

  const getWaitlistPayerName = (entry) => {
    const payerUserId = typeof entry?.payerUserId === 'string' ? entry.payerUserId.trim() : '';
    if (!payerUserId || payerUserId === entry?.id) return '';
    return formatPersonName(waitlistProfiles?.[payerUserId] || {}, 'Unknown payer');
  };

  const getParticipantName = (participant) => {
    const userId = typeof participant?.id === 'string' ? participant.id.trim() : '';
    return formatPersonName(participantProfiles?.[userId] || {}, 'Unknown member');
  };

  const getParticipantPayerName = (participant) => {
    const payerUserId = typeof participant?.payerUserId === 'string' ? participant.payerUserId.trim() : '';
    if (!payerUserId || payerUserId === participant?.id) return '';
    return formatPersonName(participantProfiles?.[payerUserId] || {}, 'Unknown payer');
  };

  const updateDraft = (patch) => setDraft((current) => ({ ...current, ...patch }));
  const updateJoinSetup = (patch) => setJoinSetup((current) => ({ ...current, ...patch }));
  const updateCourseDefaults = (patch) => setCourseDefaults((current) => ({ ...current, ...patch }));
  const setAllActiveCourseCardsVisible = () => {
    setActiveCourseVisibleCardIds(restrictableDashboardCardIds);
  };
  const handleRestrictActiveCourseCardsChange = (checked) => {
    setRestrictActiveCourseCards(checked);
    if (checked && normalizeRestrictedCardIds(activeCourseVisibleCardIds).length === 0) {
      setAllActiveCourseCardsVisible();
    }
  };
  const toggleActiveCourseVisibleCard = (cardId) => {
    const normalizedCardId = typeof cardId === 'string' ? cardId.trim() : '';
    if (!normalizedCardId) return;
    setActiveCourseVisibleCardIds((current) => {
      const currentSet = new Set(normalizeRestrictedCardIds(current));
      if (currentSet.has(normalizedCardId)) currentSet.delete(normalizedCardId);
      else currentSet.add(normalizedCardId);
      return normalizeRestrictedCardIds(Array.from(currentSet));
    });
  };
  const activeCourseVisibleCardCount = normalizeRestrictedCardIds(activeCourseVisibleCardIds)
    .filter((cardId) => restrictableDashboardCardIds.includes(cardId)).length;
  const coursePaymentSetupReady = isCoursePaymentSetupComplete(stripeSetup || {});
  const currentGeneralSettings = {
    waitlistOpen: teamWaitlistOpen === true,
    memberNestFeeHandling,
    stripeFeeHandling,
    restrictActiveCourseCards: restrictActiveCourseCards === true,
    activeCourseVisibleCardIds: normalizeRestrictedCardIds(activeCourseVisibleCardIds),
  };
  const hasUnsavedGeneralChanges = normalizeGeneralSetupSnapshot(currentGeneralSettings, courseDefaults)
    !== normalizeGeneralSetupSnapshot(savedGeneralSettings, savedCourseDefaults);

  useEffect(() => {
    onUnsavedGeneralChangesChange?.(hasUnsavedGeneralChanges);
    return () => onUnsavedGeneralChangesChange?.(false);
  }, [hasUnsavedGeneralChanges, onUnsavedGeneralChangesChange]);

  const resetGeneralSetupDraft = () => {
    setTeamWaitlistOpen(savedGeneralSettings.waitlistOpen === true);
    setMemberNestFeeHandling(savedGeneralSettings.memberNestFeeHandling === 'deduct' ? 'deduct' : 'add');
    setStripeFeeHandling(savedGeneralSettings.stripeFeeHandling === 'deduct' ? 'deduct' : 'add');
    setRestrictActiveCourseCards(savedGeneralSettings.restrictActiveCourseCards === true);
    setActiveCourseVisibleCardIds(normalizeRestrictedCardIds(savedGeneralSettings.activeCourseVisibleCardIds || []));
    setCourseDefaults(normalizeCourseDefaults(savedCourseDefaults));
  };

  const confirmDiscardGeneralChanges = () => {
    if (!hasUnsavedGeneralChanges) return true;
    const confirmed = window.confirm(UNSAVED_GENERAL_SETUP_MESSAGE);
    if (confirmed) resetGeneralSetupDraft();
    return confirmed;
  };

  const handlePanelChange = (panel) => {
    if (panel === activePanel) return;
    if (!confirmDiscardGeneralChanges()) return;
    setError('');
    setActivePanel(panel);
  };

  const handleSaveGeneralSetup = async (stripeConfirmed = false) => {
    if (!teamId) return;
    const closingWaitlist = teamWaitlistOpen !== true;
    const cleanedCourseDefaults = normalizeCourseDefaults(courseDefaults);
    const defaultRequiresWaitlist = normalizeLearnToSailSignupMode(cleanedCourseDefaults) === 'waitlist_only';
    const defaultOpensSignup = normalizeLearnToSailSignupMode(cleanedCourseDefaults) !== 'closed';
    const coursesRequiringWaitlist = courses.filter((course) => normalizeLearnToSailSignupMode(course) === 'waitlist_only');
    const defaultFeeMinor = Math.max(0, Math.round(Number(cleanedCourseDefaults.courseFeeMajor || 0) * 100));
    const paidSignupWillOpen = (defaultOpensSignup && defaultFeeMinor > 0)
      || (teamWaitlistOpen === true && courses.some((course) => Number(course.courseFeeMinor || 0) > 0));
    if (paidSignupWillOpen && !stripeConfirmed && !coursePaymentSetupReady) {
      await requireStripePayment({
        feature: 'learn_to_sail',
        featureLabel: 'Paid Learn to Sail signup',
        returnTarget: 'learn_to_sail',
        resumeEntity: 'general',
        onReady: () => handleSaveGeneralSetup(true),
      });
      return;
    }
    if (closingWaitlist && (defaultRequiresWaitlist || coursesRequiringWaitlist.length > 0)) {
      setError('Open the generic Learn to Sail waitlist, or change all Admin release courses and defaults before saving it as closed.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await Promise.all([
        updateDoc(doc(db, 'teams', teamId), {
          'modules.sailing.learnToSail.waitlistOpen': teamWaitlistOpen === true,
          'modules.sailing.learnToSail.memberNestFeeHandling': memberNestFeeHandling === 'deduct' ? 'deduct' : 'add',
          'modules.sailing.learnToSail.stripeFeeHandling': stripeFeeHandling === 'deduct' ? 'deduct' : 'add',
          'modules.sailing.learnToSail.restrictActiveCourseCards': restrictActiveCourseCards === true,
          'modules.sailing.learnToSail.activeCourseVisibleCardIds': normalizeRestrictedCardIds(activeCourseVisibleCardIds),
        }),
        setDoc(doc(db, 'teams', teamId, 'learnToSailConfig', 'config'), {
          courseDefaults: {
            ...cleanedCourseDefaults,
            courseFeeMinor: Math.max(0, Math.round(Number(cleanedCourseDefaults.courseFeeMajor || 0) * 100)),
          },
        }, { merge: true }),
      ]);
      setCourseDefaults(cleanedCourseDefaults);
      setSavedCourseDefaults(cleanedCourseDefaults);
      setSavedGeneralSettings({
        waitlistOpen: teamWaitlistOpen === true,
        memberNestFeeHandling: memberNestFeeHandling === 'deduct' ? 'deduct' : 'add',
        stripeFeeHandling: stripeFeeHandling === 'deduct' ? 'deduct' : 'add',
        restrictActiveCourseCards: restrictActiveCourseCards === true,
        activeCourseVisibleCardIds: normalizeRestrictedCardIds(activeCourseVisibleCardIds),
      });
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to save Learn to Sail general setup.'));
    } finally {
      setSaving(false);
    }
  };

  const selectCourse = (courseId) => {
    if (!confirmDiscardGeneralChanges()) return;
    setError('');
    setActivePanel('courses');
    if (courseId === '__new__') {
      setActiveCourseId('__new__');
      setDraft(emptyCourse(courseDefaults, savedGeneralSettings));
      return;
    }
    const course = courses.find((entry) => entry.id === courseId);
    setActiveCourseId(courseId);
    setDraft(normalizeCourseFromDoc(course || emptyCourse(courseDefaults, savedGeneralSettings), savedGeneralSettings));
  };

  const updateSession = (sessionId, patch) => {
    updateDraft({
      sessions: (draft.sessions || []).map((session) => (
        session.id === sessionId ? { ...session, ...patch } : session
      )),
    });
  };

  const addSession = () => {
    updateDraft({
      sessions: [...(draft.sessions || []), createLearnToSailSession()],
    });
  };

  const copySession = (sessionId) => {
    const sessionToCopy = (draft.sessions || []).find((session) => session.id === sessionId);
    if (!sessionToCopy) return;
    const nextSessions = [];
    (draft.sessions || []).forEach((session) => {
      nextSessions.push(session);
      if (session.id === sessionId) {
        nextSessions.push(createLearnToSailSession({
          date: sessionToCopy.date,
          startTime: sessionToCopy.startTime,
          endTime: sessionToCopy.endTime,
          assetReservations: (sessionToCopy.assetReservations || []).map((reservation) => ({
            ...reservation,
            id: `reservation_${crypto.randomUUID()}`,
          })),
        }));
      }
    });
    updateDraft({ sessions: nextSessions });
  };

  const removeSession = (sessionId) => {
    const remaining = (draft.sessions || []).filter((session) => session.id !== sessionId);
    updateDraft({ sessions: remaining.length > 0 ? remaining : [createLearnToSailSession()] });
  };

  const handleLocationSelect = (locationId) => {
    const nextLocationId = String(locationId || '').trim();
    const selectedLocation = locations.find((location) => location.id === nextLocationId) || null;
    updateDraft({
      locationId: nextLocationId,
      locationName: selectedLocation?.name || '',
      locationStreet: selectedLocation?.street || '',
      locationCity: selectedLocation?.city || '',
      locationPostcode: selectedLocation?.postcode || '',
      locationCountry: selectedLocation?.country || '',
    });
  };

  const handleCourseDefaultLocationSelect = (locationId) => {
    const nextLocationId = String(locationId || '').trim();
    const selectedLocation = locations.find((location) => location.id === nextLocationId) || null;
    updateCourseDefaults({
      locationId: nextLocationId,
      locationName: selectedLocation?.name || '',
      locationStreet: selectedLocation?.street || '',
      locationCity: selectedLocation?.city || '',
      locationPostcode: selectedLocation?.postcode || '',
      locationCountry: selectedLocation?.country || '',
    });
  };

  const updateJoinQuestion = (questionId, patch) => {
    updateJoinSetup({
      questions: (joinSetup.questions || []).map((question) => (
        question.id === questionId
          ? {
              ...question,
              ...patch,
              ...(patch.type && patch.type !== 'select' ? { options: [] } : {}),
              ...(patch.type === 'checkbox' ? { required: true } : {}),
            }
          : question
      )),
    });
  };

  const addJoinQuestion = () => {
    updateJoinSetup({
      questions: [...(joinSetup.questions || []), createLearnToSailJoinQuestion()],
    });
  };

  const removeJoinQuestion = (questionId) => {
    updateJoinSetup({
      questions: (joinSetup.questions || []).filter((question) => question.id !== questionId),
    });
  };

  const handleSaveJoinSetup = async () => {
    setSaving(true);
    setError('');
    try {
      const cleanedQuestions = normalizeLearnToSailJoinQuestions(joinSetup.questions || [])
        .filter((question) => typeof question.label === 'string' && question.label.trim() !== '')
        .filter((question) => question.type !== 'select' || (question.options || []).length > 0)
        .map((question) => ({
          id: question.id,
          label: question.label.trim(),
          type: question.type,
          required: question.type === 'checkbox' ? true : question.required === true,
          syncToProfile: question.syncToProfile === true,
          options: question.type === 'select' ? normalizeQuestionOptions(question.options) : [],
        }));
      await setDoc(doc(db, 'teams', teamId, 'learnToSailJoinConfig', 'config'), {
        reviewMode: joinSetup.reviewMode === 'auto_waitlist' ? 'auto_waitlist' : 'approval_required',
        questionMode: joinSetup.questionMode,
        indicativePriceMajor: typeof joinSetup.indicativePriceMajor === 'string' ? joinSetup.indicativePriceMajor : '',
        indicativePriceMinor: Math.max(0, Math.round(Number(joinSetup.indicativePriceMajor || 0) * 100)),
        indicativePriceNote: typeof joinSetup.indicativePriceNote === 'string' ? joinSetup.indicativePriceNote.trim() : '',
        questions: cleanedQuestions,
      }, { merge: true });
      setJoinSetup({
        reviewMode: joinSetup.reviewMode === 'auto_waitlist' ? 'auto_waitlist' : 'approval_required',
        questionMode: joinSetup.questionMode,
        indicativePriceMajor: typeof joinSetup.indicativePriceMajor === 'string' ? joinSetup.indicativePriceMajor : '',
        indicativePriceNote: typeof joinSetup.indicativePriceNote === 'string' ? joinSetup.indicativePriceNote.trim() : '',
        questions: cleanedQuestions,
      });
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to save Learn to Sail join setup.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async (key, url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLinkKey(key);
      window.setTimeout(() => {
        setCopiedLinkKey((current) => (current === key ? '' : current));
      }, 1500);
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Could not copy Learn to Sail link.'));
    }
  };

  const handleSave = async (stripeConfirmed = false) => {
    const courseName = String(draft.name || '').trim();
    if (!courseName) {
      setError('Course name is required.');
      return;
    }
    if (normalizedSessions.length === 0) {
      setError('Add at least one complete session with a date, start time, and end time.');
      return;
    }
    if (!(finalSessionEnd instanceof Date) || Number.isNaN(finalSessionEnd.getTime())) {
      setError('The final session end time is invalid.');
      return;
    }
    const capacity = Math.max(1, Number(draft.capacity || 1));
    const postCoursePurgeDays = Math.max(1, Number(draft.postCoursePurgeDays || 1));
    const courseFeeMinor = Math.max(0, Math.round(Number(draft.courseFeeMajor || 0) * 100));
    const conversionStartMode = normalizeLearnToSailConversionStartMode(draft.conversionStartMode);
    const manualConversionStartAtInput = formatLearnToSailDateInputValue(draft.manualConversionStartAt || '');
    if (conversionStartMode === 'manual_date' && !manualConversionStartAtInput) {
      setError('Choose the lockout date before saving this course.');
      return;
    }
    const existingCourse = activeCourseId === '__new__'
      ? null
      : courses.find((course) => course.id === activeCourseId) || null;
    const courseId = existingCourse?.id
      || (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `lts_course_${Date.now()}`);
    const nextCourseTag = getLearnToSailCourseTagName(courseName);
    const previousCourseTag = getLearnToSailCourseTagName(existingCourse?.name || '');
    const nextConversionStartsAt = calculateLearnToSailConversionStartsAt(normalizedSessions, {
      conversionStartMode,
      manualConversionStartAt: manualConversionStartAtInput,
    });
    if (!(nextConversionStartsAt instanceof Date) || Number.isNaN(nextConversionStartsAt.getTime())) {
      setError('The lockout start date is invalid.');
      return;
    }
    if (nextConversionStartsAt.getTime() < finalSessionEnd.getTime()) {
      setError('The lockout start date must be on or after the final session end.');
      return;
    }
    const nextPurgeAt = calculateLearnToSailPurgeAt(normalizedSessions, postCoursePurgeDays, {
      conversionStartMode,
      manualConversionStartAt: manualConversionStartAtInput,
    });
    const signupMode = normalizeLearnToSailSignupMode(draft);
    const draftFeeSettings = getLearnToSailCourseFeeSettings({ ...draft, courseFeeMinor }, savedGeneralSettings);
    if (signupMode !== 'closed' && courseFeeMinor > 0 && !stripeConfirmed && !coursePaymentSetupReady) {
      await requireStripePayment({
        feature: 'learn_to_sail',
        featureLabel: 'Paid Learn to Sail signup',
        returnTarget: 'learn_to_sail',
        resumeEntity: activeCourseId && activeCourseId !== '__new__' ? activeCourseId : 'new',
        onReady: () => handleSave(true),
      });
      return;
    }
    if (signupMode === 'waitlist_only' && teamWaitlistOpen !== true) {
      setError('Admin release courses need the generic Learn to Sail waitlist to be open. Open the waitlist in General, or choose Closed/Public first come.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const courseRef = doc(db, 'teams', teamId, 'learnToSailCourses', courseId);
      const payload = {
        name: courseName,
        status: draft.status === 'cancelled' ? 'cancelled' : 'active',
        description: String(draft.description || '').trim(),
        signupMode,
        publicSignupEnabled: signupMode === 'open_to_all',
        requireSessionRsvp: draft.requireSessionRsvp !== false,
        capacity,
        waitlistEnabled: true,
        courseFeeMinor,
        memberNestFeeHandling: draftFeeSettings.memberNestFeeHandling,
        stripeFeeHandling: draftFeeSettings.stripeFeeHandling,
        postCoursePurgeDays,
        conversionStartMode,
        manualConversionStartAt: conversionStartMode === 'manual_date' ? nextConversionStartsAt.toISOString() : '',
        membershipPricingMode: draft.membershipPricingMode === 'credit_course_fee' ? 'credit_course_fee' : 'full_price',
        membershipSelectionMode: draft.membershipSelectionMode === 'fixed_pricing_tag' ? 'fixed_pricing_tag' : 'member_picks',
        fixedMembershipPricingTag: draft.membershipSelectionMode === 'fixed_pricing_tag'
          ? String(draft.fixedMembershipPricingTag || '').trim()
          : '',
        locationId: String(draft.locationId || '').trim(),
        locationName: String(draft.locationName || '').trim(),
        locationStreet: String(draft.locationStreet || '').trim(),
        locationCity: String(draft.locationCity || '').trim(),
        locationPostcode: String(draft.locationPostcode || '').trim(),
        locationCountry: String(draft.locationCountry || '').trim(),
        sessions: normalizedSessions.map((session, index) => ({
          ...session,
          order: index,
          eventId: buildLearnToSailEventDocId(courseId, session.id),
        })),
        finalSessionEndsAt: finalSessionEnd.toISOString(),
        conversionStartsAt: nextConversionStartsAt.toISOString(),
        purgeAt: nextPurgeAt ? nextPurgeAt.toISOString() : '',
        updatedAt: serverTimestamp(),
      };
      await saveCourseAssets({
        teamId,
        courseType: 'learn_to_sail',
        courseId,
        courseName,
        sessions: payload.status === 'cancelled' ? [] : payload.sessions,
        state: 'live',
      });
      await setDoc(courseRef, existingCourse ? payload : {
        ...payload,
        createdAt: serverTimestamp(),
      }, { merge: true });

      const teamRef = doc(db, 'teams', teamId);
      const nextTags = upsertLearnToSailSystemTags(team?.tags || [], { courseId, courseName });
      await updateDoc(teamRef, { tags: nextTags });

      const existingSessions = normalizeLearnToSailSessions(existingCourse?.sessions || []);
      const nextEventIds = new Set(payload.sessions.map((session) => session.eventId));
      await Promise.all(
        existingSessions
          .map((session) => buildLearnToSailEventDocId(courseId, session.id))
          .filter((eventId) => eventId && !nextEventIds.has(eventId))
          .map((eventId) => deleteDoc(doc(db, 'teams', teamId, 'events', eventId)))
      );
      await Promise.all(
        payload.sessions.map((session, index) => setDoc(
          doc(db, 'teams', teamId, 'events', session.eventId),
          {
            ...buildLearnToSailEventPayload({
              course: {
                ...payload,
                courseFeeMinor,
              },
              courseId,
              session,
              sessionIndex: index,
            }),
            capacity: Math.max(1, Number(payload.capacity || 1)),
            ...(existingSessions.some((existingSession) => existingSession.id === session.id) ? {} : { createdAt: serverTimestamp() }),
          },
          { merge: true }
        ))
      );

      if (existingCourse && previousCourseTag && previousCourseTag !== nextCourseTag) {
        await migrateTagNameAcrossCollection(collection(db, 'teams', teamId, 'members'), previousCourseTag, nextCourseTag);
        await migrateTagNameAcrossCollection(collection(db, 'teams', teamId, 'learnToSailCourses', courseId, 'participants'), previousCourseTag, nextCourseTag);
      }

      const refreshedSnapshot = await getDocs(query(collection(db, 'teams', teamId, 'learnToSailCourses'), orderBy('updatedAt', 'desc')));
      const refreshedCourses = refreshedSnapshot.docs.map((docSnap) => normalizeCourseFromDoc({ id: docSnap.id, ...docSnap.data() }, savedGeneralSettings));
      setCourses(refreshedCourses);
      setActiveCourseId(courseId);
      setDraft(normalizeCourseFromDoc(refreshedCourses.find((course) => course.id === courseId) || { id: courseId, ...payload }, savedGeneralSettings));
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to save Learn to Sail course.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeCourseId || activeCourseId === '__new__') {
      onClose?.();
      return;
    }
    const currentCourse = courses.find((course) => course.id === activeCourseId);
    if (!currentCourse) return;
    const courseName = currentCourse.name || 'this course';
    const confirmed = window.prompt(
      `Delete "${courseName}" and remove its generated events, participant records, and course tags?\n\nType the course name to confirm.`
    ) === courseName;
    if (!confirmed) return;
    setSaving(true);
    setError('');
    try {
      const currentCourseTag = getLearnToSailCourseTagName(currentCourse?.name || '');
      const sessions = normalizeLearnToSailSessions(currentCourse?.sessions || []);
      await releaseCourseAssets({ teamId, courseType: 'learn_to_sail', courseId: activeCourseId, courseName });
      await Promise.all(
        sessions.map((session) => deleteDoc(doc(db, 'teams', teamId, 'events', buildLearnToSailEventDocId(activeCourseId, session.id))))
      );
      const participantCollection = collection(db, 'teams', teamId, 'learnToSailCourses', activeCourseId, 'participants');
      const participantSnapshot = await getDocs(participantCollection);
      await Promise.all(participantSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
      await stripTagNameAcrossCollection(collection(db, 'teams', teamId, 'members'), currentCourseTag);
      await deleteDoc(doc(db, 'teams', teamId, 'learnToSailCourses', activeCourseId));

      const remainingCourses = courses.filter((course) => course.id !== activeCourseId);
      const remainingTags = (team?.tags || []).filter((tag) => {
        if (tag?.name === currentCourseTag) return false;
        if (remainingCourses.length === 0 && isLearnToSailSystemTag(tag)) return false;
        return true;
      });
      await updateDoc(doc(db, 'teams', teamId), { tags: remainingTags });
      setCourses(remainingCourses);
      if (remainingCourses.length > 0) {
        setActiveCourseId(remainingCourses[0].id);
        setDraft(normalizeCourseFromDoc(remainingCourses[0], savedGeneralSettings));
      } else {
        setActiveCourseId('__new__');
        setDraft(emptyCourse(courseDefaults, savedGeneralSettings));
      }
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to delete Learn to Sail course.'));
    } finally {
      setSaving(false);
    }
  };

  const refreshWaitlistEntries = async () => {
    const waitlistSnapshot = await getDocs(query(collection(db, 'teams', teamId, 'learnToSailWaitlist'), orderBy('createdAt', 'asc')));
    setWaitlistEntries(waitlistSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
  };

  const handleApproveWaitlist = async (userId) => {
    if (!teamId || !userId) return;
    setSaving(true);
    setError('');
    try {
      const nextQueuePosition = approvedWaitlistEntries.reduce((max, entry) => {
        const value = Number(entry?.queuePosition);
        return Number.isFinite(value) ? Math.max(max, value) : max;
      }, 0) + 1;
      await setDoc(doc(db, 'teams', teamId, 'learnToSailWaitlist', userId), {
        status: 'waitlisted',
        queuePosition: nextQueuePosition,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await refreshWaitlistEntries();
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to approve Learn to Sail waitlist application.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReleaseWaitlist = async (userId, stripeConfirmed = false) => {
    if (!teamId || !activeCourseId || !userId) return;
    const releaseFeeMinor = Math.max(0, Number(draft.courseFeeMinor || Math.round(Number(draft.courseFeeMajor || 0) * 100)));
    if (releaseFeeMinor > 0 && !stripeConfirmed && !coursePaymentSetupReady) {
      await requireStripePayment({
        feature: 'learn_to_sail',
        featureLabel: 'Paid Learn to Sail waitlist places',
        returnTarget: 'learn_to_sail',
        resumeEntity: activeCourseId,
        onReady: () => handleReleaseWaitlist(userId, true),
      });
      return;
    }
    setSaving(true);
    setError('');
    try {
      const releaseWaitlist = httpsCallable(functions, 'releaseLearnToSailWaitlistEntry');
      await releaseWaitlist({
        teamId,
        courseId: activeCourseId,
        userId,
      });
      const snapshot = await getDocs(collection(db, 'teams', teamId, 'learnToSailCourses', activeCourseId, 'participants'));
      setParticipants(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      await refreshWaitlistEntries();
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to release waitlisted participant.'));
    } finally {
      setSaving(false);
    }
  };

  const handleMoveApprovedWaitlistEntry = async (userId, direction) => {
    if (!teamId || !userId) return;
    const currentIndex = approvedWaitlistEntries.findIndex((entry) => entry.id === userId);
    if (currentIndex < 0) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= approvedWaitlistEntries.length) return;
    const reordered = [...approvedWaitlistEntries];
    const [movedEntry] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedEntry);
    setSaving(true);
    setError('');
    try {
      const batch = writeBatch(db);
      reordered.forEach((entry, index) => {
        batch.set(doc(db, 'teams', teamId, 'learnToSailWaitlist', entry.id), {
          queuePosition: index + 1,
          updatedAt: serverTimestamp(),
        }, { merge: true });
      });
      await batch.commit();
      await refreshWaitlistEntries();
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to reorder the Learn to Sail waitlist.'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkFillWaitlist = async (stripeConfirmed = false) => {
    if (!teamId || !activeCourseId || activeCourseId === '__new__') return;
    const releaseFeeMinor = Math.max(0, Number(draft.courseFeeMinor || Math.round(Number(draft.courseFeeMajor || 0) * 100)));
    if (releaseFeeMinor > 0 && !stripeConfirmed && !coursePaymentSetupReady) {
      await requireStripePayment({
        feature: 'learn_to_sail',
        featureLabel: 'Paid Learn to Sail waitlist places',
        returnTarget: 'learn_to_sail',
        resumeEntity: activeCourseId,
        onReady: () => handleBulkFillWaitlist(true),
      });
      return;
    }
    const releaseLimit = Math.max(1, Math.floor(Number(bulkFillCount || 1)));
    const releasableCount = Math.min(releaseLimit, approvedWaitlistEntries.length, currentCourseAvailableSlots);
    if (releasableCount <= 0) {
      setError('There are no approved waitlist places available to pull onto this course.');
      return;
    }
    const confirmed = window.confirm(`Release the next ${releasableCount} ${releasableCount === 1 ? 'person' : 'people'} from the Learn to Sail waitlist onto ${draft.name || 'this course'}?`);
    if (!confirmed) return;
    setSaving(true);
    setError('');
    try {
      const releaseWaitlist = httpsCallable(functions, 'releaseLearnToSailWaitlistEntry');
      for (const entry of approvedWaitlistEntries.slice(0, releasableCount)) {
        await releaseWaitlist({
          teamId,
          courseId: activeCourseId,
          userId: entry.id,
        });
      }
      const snapshot = await getDocs(collection(db, 'teams', teamId, 'learnToSailCourses', activeCourseId, 'participants'));
      setParticipants(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      await refreshWaitlistEntries();
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to bulk fill the Learn to Sail course from the waitlist.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleWaitlistEntryDetails = (userId) => {
    setExpandedWaitlistEntries((current) => ({
      ...current,
      [userId]: !current?.[userId],
    }));
  };

  const handleRemoveWaitlist = async (userId) => {
    if (!teamId || !userId) return;
    const waitlistEntry = waitlistEntries.find((entry) => entry.id === userId);
    const displayName = getWaitlistEntryName(waitlistEntry || { id: userId });
    const confirmed = window.confirm(`Remove ${displayName} from the Learn to Sail waitlist?`);
    if (!confirmed) return;
    setSaving(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'teams', teamId, 'learnToSailWaitlist', userId));
      setExpandedWaitlistEntries((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
      await refreshWaitlistEntries();
    } catch (err) {
      setError(getLearnToSailErrorMessage(err, 'Failed to remove waitlist entry.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-form outings-modal">
      <div className="outings-modal__header">
        <div>
          <h2 className="modal-title">Learn to Sail</h2>
          <p className="text-muted outings-modal__header-copy">
            Manage course cohorts, the shared waitlist, and the dedicated Learn to Sail join route.
          </p>
        </div>
      </div>

      {error ? <ErrorMessage message={error} /> : null}

      <div className="outings-modal__layout">
        <aside className="card outings-modal__sidebar" style={{ padding: 16 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <button
              type="button"
              className={activePanel === 'general' ? 'outings-modal__sidebar-item outings-modal__sidebar-item--active' : 'outings-modal__sidebar-item'}
              onClick={() => handlePanelChange('general')}
            >
              General Setup
            </button>
            <button
              type="button"
              className={activePanel === 'courses' ? 'outings-modal__sidebar-item outings-modal__sidebar-item--active' : 'outings-modal__sidebar-item'}
              onClick={() => handlePanelChange('courses')}
            >
              Course Setup
            </button>
            <button
              type="button"
              className={activePanel === 'waitlist' ? 'outings-modal__sidebar-item outings-modal__sidebar-item--active' : 'outings-modal__sidebar-item'}
              onClick={() => handlePanelChange('waitlist')}
            >
              Waitlist
            </button>
            <button
              type="button"
              className={activePanel === 'joinSetup' ? 'outings-modal__sidebar-item outings-modal__sidebar-item--active' : 'outings-modal__sidebar-item'}
              onClick={() => handlePanelChange('joinSetup')}
            >
              Join Setup
            </button>
          </div>

          <div className="outings-modal__sidebar-head">
            <div>
              <div className="card-subtitle">Courses</div>
              <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                {loading
                  ? 'Loading…'
                  : `${filteredCourses.length} ${courseListFilter === 'past' ? 'past' : 'upcoming'} cohort${filteredCourses.length === 1 ? '' : 's'}`}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              className={activeCourseId === '__new__' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
              onClick={() => selectCourse('__new__')}
            >
              New
            </button>
            <button
              type="button"
              className={courseListFilter === 'upcoming' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
              onClick={() => setCourseListFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              type="button"
              className={courseListFilter === 'past' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
              onClick={() => setCourseListFilter('past')}
            >
              Past
            </button>
          </div>

          <div className="outings-modal__sidebar-list" style={{ marginTop: 12 }}>
            {filteredCourses.map((course) => (
              <button
                key={course.id}
                type="button"
                className={activeCourseId === course.id ? 'outings-modal__sidebar-item outings-modal__sidebar-item--active' : 'outings-modal__sidebar-item'}
                onClick={() => selectCourse(course.id)}
              >
                <div style={{ width: '100%' }}>
                  <strong>{course.name || 'Untitled course'}</strong>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {normalizeLearnToSailSessions(course.sessions || []).length} session{normalizeLearnToSailSessions(course.sessions || []).length === 1 ? '' : 's'} · {Number(course.capacity || 0)} places
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Fee: {formatMoney(Math.max(0, Math.round(Number(course.courseFeeMinor || 0))), stripeSetup?.currency || 'GBP')}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {formatCourseAccessLabel(course, teamWaitlistOpen)}
                  </div>
                </div>
              </button>
            ))}
            {!loading && filteredCourses.length === 0 ? (
              <div className="text-muted" style={{ fontSize: '0.9rem', padding: '0.6rem 0.1rem' }}>
                No {courseListFilter} Learn to Sail courses yet.
              </div>
            ) : null}
          </div>
        </aside>

        <div className="outings-modal__main">
          {activePanel === 'general' ? (
            <>
              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Reusable Learn to Sail settings</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      These settings apply across the club-wide Learn to Sail waitlist and prefill new course payment settings.
                    </div>
                  </div>
                </div>

                <div className="outings-modal__details-grid" style={{ marginTop: 16 }}>
                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Generic Learn to Sail waitlist</span>
                    <select
                      className="input"
                      value={teamWaitlistOpen ? 'open' : 'closed'}
                      onChange={(event) => setTeamWaitlistOpen(event.target.value === 'open')}
                    >
                      <option value="closed">Closed</option>
                      <option value="open">Open</option>
                    </select>
                  </label>

                  <label>
                    <span>Default Member Nest fee</span>
                    <select
                      className="input"
                      value={memberNestFeeHandling}
                      onChange={(event) => setMemberNestFeeHandling(event.target.value === 'deduct' ? 'deduct' : 'add')}
                    >
                      <option value="add">Add to learner total</option>
                      <option value="deduct">Deduct from club payout</option>
                    </select>
                  </label>

                  <label>
                    <span>Default Stripe fee</span>
                    <select
                      className="input"
                      value={stripeFeeHandling}
                      onChange={(event) => setStripeFeeHandling(event.target.value === 'deduct' ? 'deduct' : 'add')}
                    >
                      <option value="add">Add to learner total</option>
                      <option value="deduct">Deduct from club payout</option>
                    </select>
                  </label>
                </div>

                <div className="text-muted outings-modal__callout outings-modal__callout--soft" style={{ marginTop: 16 }}>
                  If a course is set to subtract the Learn to Sail cost from membership later, the credit uses the net amount the club actually keeps from the course after any fees you choose to deduct from payout. Fees added on top for the learner are never credited.
                </div>

                <div className="ltr-card-restriction">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={restrictActiveCourseCards}
                    className={`ltr-card-restriction__master ${restrictActiveCourseCards ? 'is-on' : ''}`}
                    onClick={() => handleRestrictActiveCourseCardsChange(!restrictActiveCourseCards)}
                  >
                    <span className="ltr-card-restriction__copy">
                      <span className="ltr-card-restriction__title">Restrict active Learn to Sail dashboard cards</span>
                      <span className="text-muted ltr-card-restriction__description">
                        Start with all cards visible, then untick only the cards active course participants should not be able to use.
                        Manage Membership is always available.
                      </span>
                    </span>
                    <TogglePill />
                  </button>

                  {restrictActiveCourseCards ? (
                    <div className="ltr-card-restriction__panel">
                      <div className="ltr-card-restriction__panel-header">
                        <div>
                          <div className="card-subtitle">Cards visible during the active course</div>
                          <div className="text-muted ltr-card-restriction__count">
                            {activeCourseVisibleCardCount} of {restrictableDashboardCards.length} optional cards selected.
                          </div>
                        </div>
                        <div className="ltr-card-restriction__actions">
                          <button type="button" className="btn btn--secondary btn--xs" onClick={setAllActiveCourseCardsVisible}>
                            Select all
                          </button>
                          <button type="button" className="btn btn--secondary btn--xs" onClick={() => setActiveCourseVisibleCardIds([])}>
                            Clear optional
                          </button>
                        </div>
                      </div>

                      <div className="ltr-card-restriction__grid">
                        <div className="ltr-card-restriction__card ltr-card-restriction__card--locked is-on">
                          <span className="ltr-card-restriction__card-copy">
                            <span className="ltr-card-restriction__card-title">Manage Membership</span>
                            <span className="text-muted ltr-card-restriction__card-note">Always visible</span>
                          </span>
                          <TogglePill />
                        </div>
                        {restrictableDashboardCards.map((card) => {
                          const checked = activeCourseVisibleCardIds.includes(card.id);
                          return (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={checked}
                              key={card.id}
                              className={`ltr-card-restriction__card ${checked ? 'is-on' : ''}`}
                              onClick={() => toggleActiveCourseVisibleCard(card.id)}
                            >
                              <span className="ltr-card-restriction__card-title">{card.label}</span>
                              <TogglePill />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="ltr-card-restriction__footer">
                  <button type="button" className="btn btn--primary" onClick={() => handleSaveGeneralSetup()} disabled={saving || !hasUnsavedGeneralChanges}>
                    {saving ? 'Saving…' : 'Save general setup'}
                  </button>
                </div>
              </section>

              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Default course template</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      These values prefill a new Learn to Sail course when you start it. They do not update existing courses or overwrite the course currently being edited.
                    </div>
                  </div>
                </div>

                <div className="outings-modal__details-grid" style={{ marginTop: 16 }}>
                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Default description</span>
                    <textarea
                      className="input"
                      rows={4}
                      value={courseDefaults.description}
                      onChange={(event) => updateCourseDefaults({ description: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>Default signup access</span>
                    <select
                      className="input"
                      value={courseDefaults.signupMode}
                      onChange={(event) => updateCourseDefaults({ signupMode: event.target.value })}
                    >
                      <option value="closed">Closed</option>
                      <option value="waitlist_only">Admin release via generic waitlist</option>
                      <option value="open_to_all">Public first come</option>
                    </select>
                  </label>

                  <label>
                    <span>Default post-signup RSVP step</span>
                    <select
                      className="input"
                      value={courseDefaults.requireSessionRsvp ? 'required' : 'optional'}
                      onChange={(event) => updateCourseDefaults({ requireSessionRsvp: event.target.value === 'required' })}
                    >
                      <option value="required">Require learners to RSVP</option>
                      <option value="optional">Skip RSVP step after signup</option>
                    </select>
                  </label>

                  {courseDefaults.signupMode === 'waitlist_only' && !teamWaitlistOpen ? (
                    <p className="text-muted" style={{ gridColumn: '1 / -1', margin: 0, color: '#92400e', fontWeight: 600 }}>
                      Open the generic Learn to Sail waitlist before using Admin release as the default.
                    </p>
                  ) : null}

                  <label>
                    <span>Default capacity</span>
                    <input
                      className="input"
                      type="number"
                      min="1"
                      value={courseDefaults.capacity}
                      onChange={(event) => updateCourseDefaults({ capacity: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>Default course fee</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={courseDefaults.courseFeeMajor}
                      onChange={(event) => updateCourseDefaults({ courseFeeMajor: event.target.value })}
                      placeholder="0.00"
                    />
                  </label>

                  <label>
                    <span>Default purge days after course</span>
                    <input
                      className="input"
                      type="number"
                      min="1"
                      value={courseDefaults.postCoursePurgeDays}
                      onChange={(event) => updateCourseDefaults({ postCoursePurgeDays: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>Default lock out starts</span>
                    <select
                      className="input"
                      value={courseDefaults.conversionStartMode}
                      onChange={(event) => updateCourseDefaults({ conversionStartMode: event.target.value })}
                    >
                      <option value="after_course_end">After course end</option>
                      <option value="manual_date">On chosen date</option>
                    </select>
                  </label>

                  <label>
                    <span>Default membership pricing</span>
                    <select
                      className="input"
                      value={courseDefaults.membershipPricingMode}
                      onChange={(event) => updateCourseDefaults({ membershipPricingMode: event.target.value })}
                    >
                      <option value="full_price">Pay full membership</option>
                      <option value="credit_course_fee">Subtract course fee once</option>
                    </select>
                  </label>

                  <label>
                    <span>Default membership selection</span>
                    <select
                      className="input"
                      value={courseDefaults.membershipSelectionMode}
                      onChange={(event) => updateCourseDefaults({ membershipSelectionMode: event.target.value })}
                    >
                      <option value="member_picks">Member chooses pricing tag</option>
                      <option value="fixed_pricing_tag">Route to one pricing tag</option>
                    </select>
                  </label>

                  {courseDefaults.membershipSelectionMode === 'fixed_pricing_tag' ? (
                    <label style={{ gridColumn: '1 / -1' }}>
                      <span>Default fixed membership pricing tag</span>
                      <select
                        className="input"
                        value={courseDefaults.fixedMembershipPricingTag}
                        onChange={(event) => updateCourseDefaults({ fixedMembershipPricingTag: event.target.value })}
                      >
                        <option value="">Choose a pricing tag</option>
                        {editablePricingTags.map((tag) => (
                          <option key={tag.name} value={tag.name}>{tag.name}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Course location</span>
                    <select
                      className="input"
                      value={courseDefaults.locationId}
                      onChange={(event) => handleCourseDefaultLocationSelect(event.target.value)}
                    >
                      <option value="">No saved location</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>{location.name || 'Unnamed location'}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Location name</span>
                    <input
                      className="input"
                      value={courseDefaults.locationName}
                      onChange={(event) => updateCourseDefaults({ locationId: '', locationName: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>Street</span>
                    <input
                      className="input"
                      value={courseDefaults.locationStreet}
                      onChange={(event) => updateCourseDefaults({ locationId: '', locationStreet: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>City</span>
                    <input
                      className="input"
                      value={courseDefaults.locationCity}
                      onChange={(event) => updateCourseDefaults({ locationId: '', locationCity: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>Postcode</span>
                    <input
                      className="input"
                      value={courseDefaults.locationPostcode}
                      onChange={(event) => updateCourseDefaults({ locationId: '', locationPostcode: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>Country</span>
                    <input
                      className="input"
                      value={courseDefaults.locationCountry}
                      onChange={(event) => updateCourseDefaults({ locationId: '', locationCountry: event.target.value })}
                    />
                  </label>
                </div>

                <div className="text-muted outings-modal__callout outings-modal__callout--soft" style={{ marginTop: 16 }}>
                  New course starts from this template. Dates, times, course name, and any course-specific pricing can still be changed per intake.
                </div>
              </section>

              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Default course fee example</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      Preview based on the reusable default course fee above.
                    </div>
                  </div>
                </div>

                <div className="availability-status-list" style={{ marginTop: 16 }}>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">🏷️</span>
                      <span>Course fee</span>
                    </div>
                    <span className="availability-pill available">{formatMoney(generalFeePreview.courseFeeMinor, generalFeePreview.currency)}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">💳</span>
                      <span>Learner pays</span>
                    </div>
                    <span className="availability-pill available">{formatMoney(generalFeePreview.chargedTotalMinor, generalFeePreview.currency)}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">🏦</span>
                      <span>Club keeps</span>
                    </div>
                    <span className="availability-pill available">{formatMoney(generalFeePreview.clubKeepsMinor, generalFeePreview.currency)}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">↩️</span>
                      <span>Max conversion credit</span>
                    </div>
                    <span className="availability-pill unavailable">{formatMoney(generalFeePreview.conversionCreditMinor, generalFeePreview.currency)}</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
                  <div className="text-muted" style={{ fontSize: '0.95rem' }}>
                    Member Nest fee: {memberNestFeeHandling === 'add' ? `${formatMoney(generalFeePreview.platformFeeMinor, generalFeePreview.currency)} added to learner total` : `${formatMoney(generalFeePreview.platformFeeMinor, generalFeePreview.currency)} deducted from payout`}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.95rem' }}>
                    Stripe fee: {stripeFeeHandling === 'add' ? `${formatMoney(generalFeePreview.stripeFeeMinor, generalFeePreview.currency)} added to learner total` : `${formatMoney(generalFeePreview.stripeFeeMinor, generalFeePreview.currency)} deducted from payout`}
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {activePanel === 'courses' ? (
            <>
              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>{activeCourseId === '__new__' ? 'New course' : draft.name || 'Edit course'}</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      Participants stay fully in the club until the lockout date, then get gated until they convert or are purged.
                    </div>
                  </div>
                </div>

                <div className="outings-modal__details-grid" style={{ marginTop: 16 }}>
                  <label>
                    <span>Course name</span>
                    <input className="input" value={draft.name} onChange={(event) => updateDraft({ name: event.target.value })} />
                  </label>

                  <label>
                    <span>Capacity</span>
                    <input className="input" type="number" min="1" value={draft.capacity} onChange={(event) => updateDraft({ capacity: event.target.value })} />
                  </label>

                  <label>
                    <span>Course fee</span>
                    <input className="input" type="number" min="0" step="0.01" value={draft.courseFeeMajor} onChange={(event) => updateDraft({ courseFeeMajor: event.target.value })} placeholder="0.00" />
                  </label>

                  <label>
                    <span>Member Nest fee</span>
                    <select
                      className="input"
                      value={draft.memberNestFeeHandling}
                      onChange={(event) => updateDraft({ memberNestFeeHandling: event.target.value === 'deduct' ? 'deduct' : 'add' })}
                    >
                      <option value="add">Add to learner total</option>
                      <option value="deduct">Deduct from club payout</option>
                    </select>
                  </label>

                  <label>
                    <span>Stripe fee</span>
                    <select
                      className="input"
                      value={draft.stripeFeeHandling}
                      onChange={(event) => updateDraft({ stripeFeeHandling: event.target.value === 'deduct' ? 'deduct' : 'add' })}
                    >
                      <option value="add">Add to learner total</option>
                      <option value="deduct">Deduct from club payout</option>
                    </select>
                  </label>

                  <label>
                    <span>Purge days after course</span>
                    <input className="input" type="number" min="1" value={draft.postCoursePurgeDays} onChange={(event) => updateDraft({ postCoursePurgeDays: event.target.value })} />
                  </label>

                  <label>
                    <span>Lock out starts</span>
                    <select
                      className="input"
                      value={draft.conversionStartMode}
                      onChange={(event) => updateDraft({
                        conversionStartMode: event.target.value,
                        ...(event.target.value === 'manual_date' ? {} : { manualConversionStartAt: '' }),
                      })}
                    >
                      <option value="after_course_end">After course end</option>
                      <option value="manual_date">On chosen date</option>
                    </select>
                  </label>

                  {draft.conversionStartMode === 'manual_date' ? (
                    <label>
                      <span>Lock out date</span>
                      <input
                        className="input"
                        type="date"
                        value={draft.manualConversionStartAt || ''}
                        onChange={(event) => updateDraft({ manualConversionStartAt: event.target.value })}
                      />
                    </label>
                  ) : null}

                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Description</span>
                    <textarea className="input" rows={4} value={draft.description} onChange={(event) => updateDraft({ description: event.target.value })} />
                  </label>

                  <div className="availability-status-list" style={{ gridColumn: '1 / -1', marginTop: 0 }}>
                    <div className="availability-status-item">
                      <div className="availability-status-label">
                        <span>Learner pays</span>
                      </div>
                      <span className="availability-pill available">{formatMoney(courseFeePreview.totalDueMinor, stripeSetup?.currency || 'GBP')}</span>
                    </div>
                    <div className="availability-status-item">
                      <div className="availability-status-label">
                        <span>Member Nest fee added</span>
                      </div>
                      <span className="availability-pill available">{formatMoney(courseFeePreview.chargedPlatformFeeMinor, stripeSetup?.currency || 'GBP')}</span>
                    </div>
                    <div className="availability-status-item">
                      <div className="availability-status-label">
                        <span>Stripe fee added</span>
                      </div>
                      <span className="availability-pill available">{formatMoney(courseFeePreview.chargedStripeFeeMinor, stripeSetup?.currency || 'GBP')}</span>
                    </div>
                  </div>

                  <label>
                    <span>Signup access</span>
                    <select
                      className="input"
                      value={draft.signupMode}
                      onChange={(event) => updateDraft({ signupMode: event.target.value })}
                    >
                      <option value="closed">Closed</option>
                      <option value="waitlist_only">Admin release via generic waitlist</option>
                      <option value="open_to_all">Public first come</option>
                    </select>
                  </label>

                  <label>
                    <span>Post-signup RSVP step</span>
                    <select
                      className="input"
                      value={draft.requireSessionRsvp ? 'required' : 'optional'}
                      onChange={(event) => updateDraft({ requireSessionRsvp: event.target.value === 'required' })}
                    >
                      <option value="required">Require learners to RSVP</option>
                      <option value="optional">Skip RSVP step after signup</option>
                    </select>
                  </label>

                  {draft.signupMode === 'waitlist_only' && !teamWaitlistOpen ? (
                    <p className="text-muted" style={{ gridColumn: '1 / -1', margin: 0, color: '#92400e', fontWeight: 600 }}>
                      Open the generic Learn to Sail waitlist in General before this course can use Admin release.
                    </p>
                  ) : null}

                  <label>
                    <span>Membership pricing</span>
                    <select className="input" value={draft.membershipPricingMode} onChange={(event) => updateDraft({ membershipPricingMode: event.target.value })}>
                      <option value="full_price">Pay full membership</option>
                      <option value="credit_course_fee">Subtract course fee once</option>
                    </select>
                  </label>

                  <label>
                    <span>Membership selection</span>
                    <select className="input" value={draft.membershipSelectionMode} onChange={(event) => updateDraft({ membershipSelectionMode: event.target.value })}>
                      <option value="member_picks">Member chooses pricing tag</option>
                      <option value="fixed_pricing_tag">Route to one pricing tag</option>
                    </select>
                  </label>

                  {draft.membershipSelectionMode === 'fixed_pricing_tag' ? (
                    <label style={{ gridColumn: '1 / -1' }}>
                      <span>Fixed membership pricing tag</span>
                      <select className="input" value={draft.fixedMembershipPricingTag} onChange={(event) => updateDraft({ fixedMembershipPricingTag: event.target.value })}>
                        <option value="">Choose a pricing tag</option>
                        {editablePricingTags.map((tag) => (
                          <option key={tag.name} value={tag.name}>{tag.name}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Saved location</span>
                    <select className="input" value={draft.locationId} onChange={(event) => handleLocationSelect(event.target.value)}>
                      <option value="">No saved location</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>{location.name || 'Unnamed location'}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Location name</span>
                    <input className="input" value={draft.locationName} onChange={(event) => updateDraft({ locationId: '', locationName: event.target.value })} />
                  </label>

                  <label>
                    <span>Street</span>
                    <input className="input" value={draft.locationStreet} onChange={(event) => updateDraft({ locationId: '', locationStreet: event.target.value })} />
                  </label>

                  <label>
                    <span>City</span>
                    <input className="input" value={draft.locationCity} onChange={(event) => updateDraft({ locationId: '', locationCity: event.target.value })} />
                  </label>

                  <label>
                    <span>Postcode</span>
                    <input className="input" value={draft.locationPostcode} onChange={(event) => updateDraft({ locationId: '', locationPostcode: event.target.value })} />
                  </label>

                  <label>
                    <span>Country</span>
                    <input className="input" value={draft.locationCountry} onChange={(event) => updateDraft({ locationId: '', locationCountry: event.target.value })} />
                  </label>
                </div>

                <div className="text-muted outings-modal__callout outings-modal__callout--soft" style={{ marginTop: 16 }}>
                  Generated sessions are RSVP events, so they can be linked into Club-led Outings. The post-signup RSVP setting only controls whether learners must answer those RSVPs before opening the club.
                  {draft.signupMode === 'waitlist_only' ? ' This course stays off the public course picker and can only be filled by releasing people from the generic Learn to Sail waitlist.' : ''}
                  {draft.signupMode === 'open_to_all' ? ' This course goes live publicly on a first-come, first-served basis. If your generic Learn to Sail waitlist is open, people already on that waitlist are not holding reserved places on this intake unless you release them manually.' : ''}
                  {draft.signupMode === 'closed' ? ' This course is fully closed to public joins until you switch it to admin-release-only or public-first-come.' : ''}
                </div>

                <div className="card" style={{ padding: 12, marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                      <div style={{ fontWeight: 600 }}>Course join link</div>
                      <div className="text-muted" style={{ marginTop: 4, wordBreak: 'break-all' }}>
                        {activeCourseId === '__new__'
                          ? 'Save this course before copying a course-specific join link.'
                          : (selectedCourseJoinUrl || selectedCourseLinkUnavailableMessage)}
                      </div>
                    </div>
                    {selectedCourseJoinUrl ? (
                      <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleCopyLink(`course-${activeCourseId}`, selectedCourseJoinUrl)}>
                        {copiedLinkKey === `course-${activeCourseId}` ? 'Copied' : 'Copy link'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Sessions</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      These dates become normal event documents for the team calendar.
                    </div>
                  </div>
                  <button type="button" className="btn btn--secondary btn--xs" onClick={addSession}>
                    Add session
                  </button>
                </div>

                <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                  {(draft.sessions || []).map((session, index) => (
                    <div key={session.id} className="card" style={{ padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                        <strong>Session {index + 1}</strong>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="btn btn--secondary btn--xs" onClick={() => copySession(session.id)}>
                            Copy
                          </button>
                          <button type="button" className="btn btn--secondary btn--xs" onClick={() => removeSession(session.id)}>
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="outings-modal__details-grid">
                        <label>
                          <span>Date</span>
                          <input className="input" type="date" value={session.date} onChange={(event) => updateSession(session.id, { date: event.target.value })} />
                        </label>

                        <label>
                          <span>Start</span>
                          <input className="input" type="time" value={session.startTime} onChange={(event) => updateSession(session.id, { startTime: event.target.value })} />
                        </label>

                        <label>
                          <span>End</span>
                          <input className="input" type="time" value={session.endTime} onChange={(event) => updateSession(session.id, { endTime: event.target.value })} />
                        </label>
                      </div>
                      <label className="checkbox-row" style={{ marginTop: 10 }}>
                        <input type="checkbox" checked={session.status === 'cancelled'} onChange={(event) => updateSession(session.id, { status: event.target.checked ? 'cancelled' : 'scheduled' })} />
                        Session cancelled
                      </label>
                      <CourseBoatReservations
                        teamId={teamId}
                        courseType="learn_to_sail"
                        session={session}
                        onChange={(assetReservations) => updateSession(session.id, { assetReservations })}
                      />
                    </div>
                  ))}
                </div>
              </section>

              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Lifecycle</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      Final session, lockout, and purge dates are derived from the saved sessions, lifecycle mode, and purge-day setting.
                    </div>
                  </div>
                </div>
                <label className="checkbox-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={draft.status === 'cancelled'} onChange={(event) => updateDraft({ status: event.target.checked ? 'cancelled' : 'active' })} />
                  Course cancelled (releases future boat reservations and closes signup)
                </label>

                <div className="availability-status-list" style={{ marginTop: 16 }}>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">🕒</span>
                      <span>Final session ends</span>
                    </div>
                    <span className="availability-pill available">{formatDateTime(finalSessionEnd)}</span>
                  </div>

                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">⏳</span>
                      <span>Lockout starts</span>
                    </div>
                    <span className="availability-pill unavailable">{formatDateTime(conversionStartsAt)}</span>
                  </div>

                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">🚪</span>
                      <span>Purge deadline</span>
                    </div>
                    <span className="availability-pill unavailable">{formatDateTime(purgeAt)}</span>
                  </div>
                </div>

                {normalizeLearnToSailSessions(draft.sessions || []).length > 0 ? (
                  <div style={{ marginTop: 16 }}>
                    <div className="card-subtitle">Session RSVPs</div>
                    <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                      {normalizeLearnToSailSessions(draft.sessions || []).map((session, index) => {
                        const responseMap = participantRsvpBySession?.[session.id] || {};
                        const yesCount = Object.values(responseMap).filter((value) => value === 'yes').length;
                        const noCount = Object.values(responseMap).filter((value) => value === 'no').length;
                        return (
                          <div key={session.id} className="card" style={{ padding: 12 }}>
                            <div style={{ fontWeight: 600 }}>Session {index + 1}</div>
                            <div className="text-muted" style={{ marginTop: 4 }}>
                              {formatDateTime(new Date(`${session.date}T${session.startTime}`))}
                            </div>
                            <div className="text-muted" style={{ marginTop: 6, fontSize: '0.9rem' }}>
                              Yes {yesCount} · No {noCount}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {activeCourseId !== '__new__' ? (
                  <div style={{ marginTop: 16 }}>
                    <div className="card-subtitle">Participants</div>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      Active {participantSummary.active_course || 0} · Released {participantSummary.released_waitlist || 0} · Awaiting conversion {participantSummary.awaiting_conversion || 0}
                      {(participantSummary.waitlisted || 0) > 0 ? ` · Legacy per-course waitlisted ${participantSummary.waitlisted || 0}` : ''}
                    </div>
                    {participants.length > 0 ? (
                      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                        {participants.map((participant) => {
                          const status = String(participant?.status || '').trim().toLowerCase();
                          const payerName = getParticipantPayerName(participant);
                          return (
                            <div key={participant.id} className="card" style={{
                              padding: 12,
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 12,
                              alignItems: 'center'
                            }}>
                              <div>
                                <strong>{getParticipantName(participant)}</strong>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  {formatParticipantStatus(status)}
                                </div>
                                {payerName ? (
                                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    Parent/payer: {payerName}
                                  </div>
                                ) : null}
                                {status === 'released_waitlist' ? (
                                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    Released: {formatTimestampLabel(participant?.releasedFromWaitlistAt, 'Not recorded')}
                                  </div>
                                ) : null}
                                {normalizeLearnToSailSessions(draft.sessions || []).length > 0 ? (
                                  <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
                                    {normalizeLearnToSailSessions(draft.sessions || []).map((session, index) => (
                                      <div key={session.id} className="text-muted" style={{ fontSize: '0.85rem' }}>
                                        Session {index + 1}: {formatRsvpStatusLabel(participantRsvpBySession?.[session.id]?.[participant.id] || '')}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                              {status === 'waitlisted' ? (
                                <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleReleaseWaitlist(participant.id)} disabled={saving}>
                                  Release to payment
                                </button>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </>
          ) : null}

          {activePanel === 'waitlist' ? (
            <>
              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Waitlist queue</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      {joinSetup.reviewMode === 'auto_waitlist'
                        ? 'Applications go straight onto the live queue ready to be pulled onto a course.'
                        : 'Applications are reviewed first, then moved into the live queue ready to be pulled onto a course.'}
                    </div>
                  </div>
                </div>

                <div className="availability-status-list" style={{ marginTop: 16 }}>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">📝</span>
                      <span>Awaiting approval</span>
                    </div>
                    <span className="availability-pill unavailable">{pendingApprovalEntries.length}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">⏳</span>
                      <span>Approved queue</span>
                    </div>
                    <span className="availability-pill available">{approvedWaitlistEntries.length}</span>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div className="card-subtitle">Applications awaiting approval</div>
                  <div className="text-muted" style={{ marginTop: 4 }}>
                    {joinSetup.reviewMode === 'auto_waitlist'
                      ? 'Auto-waitlist is enabled, so new Learn to Sail applications bypass this review step.'
                      : 'Members complete the Learn to Sail join questions first. Approve them here before they enter the live waitlist.'}
                  </div>
                  {pendingApprovalEntries.length > 0 ? (
                    <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                      {pendingApprovalEntries.map((entry) => {
                        const isExpanded = expandedWaitlistEntries?.[entry.id] === true;
                        const payerName = getWaitlistPayerName(entry);
                        return (
                          <div key={entry.id} className="card" style={{ padding: 12 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 12,
                              alignItems: 'flex-start'
                            }}>
                              <div>
                                <strong>{getWaitlistEntryName(entry)}</strong>
                                {payerName ? (
                                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    Parent/payer: {payerName}
                                  </div>
                                ) : null}
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  {entry.preferredCourseName ? `Requested: ${entry.preferredCourseName}` : 'No preferred course recorded'}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  Applied: {formatTimestampLabel(entry?.createdAt)}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  {(entry.formResponses || []).length} stored join answer{(entry.formResponses || []).length === 1 ? '' : 's'}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <button type="button" className="btn btn--secondary btn--xs" onClick={() => toggleWaitlistEntryDetails(entry.id)}>
                                  {isExpanded ? 'Hide answers' : 'View answers'}
                                </button>
                                <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleApproveWaitlist(entry.id)} disabled={saving}>
                                  Approve to waitlist
                                </button>
                                <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleRemoveWaitlist(entry.id)} disabled={saving}>
                                  Remove
                                </button>
                              </div>
                            </div>
                            {isExpanded ? (
                              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                                {(entry.formResponses || []).length > 0 ? (
                                  <div style={{ display: 'grid', gap: 6 }}>
                                    {(entry.formResponses || []).map((response) => (
                                      <div key={`${entry.id}-${response.id}`} className="text-muted" style={{ fontSize: '0.92rem' }}>
                                        <strong style={{ color: '#1f2937' }}>{response.label || response.id || 'Question'}:</strong> {formatWaitlistAnswerValue(response)}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-muted" style={{ fontSize: '0.92rem' }}>
                                    No stored join answers.
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted" style={{ marginTop: 12, marginBottom: 0 }}>
                      No Learn to Sail applications are waiting for approval.
                    </p>
                  )}
                </div>

                <div style={{ marginTop: 16 }}>
                  <div className="card-subtitle">Approved waitlist</div>
                  <div className="text-muted" style={{ marginTop: 4 }}>
                    Selected course: {activeCourseId === '__new__' ? 'none' : (draft.name || activeCourseId)}.
                  </div>
                  <div className="text-muted" style={{ marginTop: 4 }}>
                    {activeCourseId === '__new__'
                      ? 'Choose a course to pull members from the queue.'
                      : `${currentCourseAvailableSlots} place${currentCourseAvailableSlots === 1 ? '' : 's'} currently open on this course.`}
                  </div>
                  {activeCourseId !== '__new__' ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
                      <label style={{ minWidth: 120 }}>
                        <span>Fill from queue</span>
                        <input
                          className="input"
                          type="number"
                          min="1"
                          max={Math.max(1, Math.min(approvedWaitlistEntries.length, currentCourseAvailableSlots))}
                          value={bulkFillCount}
                          onChange={(event) => setBulkFillCount(event.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={() => handleBulkFillWaitlist()}
                        disabled={saving || approvedWaitlistEntries.length === 0 || currentCourseAvailableSlots <= 0}
                      >
                        Fill {Math.max(1, Math.floor(Number(bulkFillCount || 1)))} from waitlist
                      </button>
                    </div>
                  ) : null}
                  {approvedWaitlistEntries.length > 0 ? (
                    <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                      {approvedWaitlistEntries.map((entry, index) => {
                        const isExpanded = expandedWaitlistEntries?.[entry.id] === true;
                        const payerName = getWaitlistPayerName(entry);
                        return (
                          <div key={entry.id} className="card" style={{ padding: 12 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 12,
                              alignItems: 'flex-start'
                            }}>
                              <div>
                                <strong>{getWaitlistEntryName(entry)}</strong>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  Waitlist position: #{index + 1}
                                </div>
                                {payerName ? (
                                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    Parent/payer: {payerName}
                                  </div>
                                ) : null}
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  {entry.preferredCourseName ? `Requested: ${entry.preferredCourseName}` : 'No preferred course recorded'}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  Applied: {formatTimestampLabel(entry?.createdAt)}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  Approved: {formatTimestampLabel(entry?.approvedAt, 'Not recorded')}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  {(entry.formResponses || []).length} stored join answer{(entry.formResponses || []).length === 1 ? '' : 's'}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--xs"
                                  onClick={() => handleMoveApprovedWaitlistEntry(entry.id, 'up')}
                                  disabled={saving || index === 0}
                                >
                                  Move up
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--xs"
                                  onClick={() => handleMoveApprovedWaitlistEntry(entry.id, 'down')}
                                  disabled={saving || index === approvedWaitlistEntries.length - 1}
                                >
                                  Move down
                                </button>
                                <button type="button" className="btn btn--secondary btn--xs" onClick={() => toggleWaitlistEntryDetails(entry.id)}>
                                  {isExpanded ? 'Hide answers' : 'View answers'}
                                </button>
                                {activeCourseId !== '__new__' ? (
                                  <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleReleaseWaitlist(entry.id)} disabled={saving}>
                                    Release to {draft.name || 'selected course'}
                                  </button>
                                ) : null}
                                <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleRemoveWaitlist(entry.id)} disabled={saving}>
                                  Remove
                                </button>
                              </div>
                            </div>
                            {isExpanded ? (
                              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                                {(entry.formResponses || []).length > 0 ? (
                                  <div style={{ display: 'grid', gap: 6 }}>
                                    {(entry.formResponses || []).map((response) => (
                                      <div key={`${entry.id}-${response.id}`} className="text-muted" style={{ fontSize: '0.92rem' }}>
                                        <strong style={{ color: '#1f2937' }}>{response.label || response.id || 'Question'}:</strong> {formatWaitlistAnswerValue(response)}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-muted" style={{ fontSize: '0.92rem' }}>
                                    No stored join answers.
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted" style={{ marginTop: 12, marginBottom: 0 }}>
                      No one is currently in the approved Learn to Sail waitlist queue.
                    </p>
                  )}
                </div>
              </section>
            </>
          ) : null}

          {activePanel === 'joinSetup' ? (
            <>
              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Join route</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      Choose which questions the Learn to Sail join route should show, and copy the dedicated URLs.
                    </div>
                  </div>
                </div>

                <div className="outings-modal__details-grid" style={{ marginTop: 16 }}>
                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Waitlist application review</span>
                    <select className="input" value={joinSetup.reviewMode} onChange={(event) => updateJoinSetup({ reviewMode: event.target.value })}>
                      <option value="approval_required">Review before waitlist</option>
                      <option value="auto_waitlist">Auto-approve into waitlist</option>
                    </select>
                  </label>

                  <label style={{ gridColumn: '1 / -1' }}>
                    <span>Learn to Sail join form mode</span>
                    <select className="input" value={joinSetup.questionMode} onChange={(event) => updateJoinSetup({ questionMode: event.target.value })}>
                      <option value="club_join_form">Sync with club join form</option>
                      <option value="club_join_form_plus_l2s">Club join form + Learn to Sail questions</option>
                      <option value="l2s_only">Learn to Sail questions only</option>
                    </select>
                  </label>

                  <label>
                    <span>Indicative price</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={joinSetup.indicativePriceMajor || ''}
                      onChange={(event) => updateJoinSetup({ indicativePriceMajor: event.target.value })}
                      placeholder="0.00"
                    />
                  </label>

                  <label>
                    <span>Indicative price note</span>
                    <input
                      className="input"
                      value={joinSetup.indicativePriceNote || ''}
                      onChange={(event) => updateJoinSetup({ indicativePriceNote: event.target.value })}
                      placeholder="Optional short note"
                    />
                  </label>
                </div>

                <div className="availability-status-list" style={{ marginTop: 16 }}>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">🧾</span>
                      <span>Review mode</span>
                    </div>
                    <span className="availability-pill available">
                      {joinSetup.reviewMode === 'auto_waitlist' ? 'Auto waitlist' : 'Admin review'}
                    </span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">📋</span>
                      <span>Club join questions</span>
                    </div>
                    <span className="availability-pill available">{clubJoinQuestions.length}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">🚣</span>
                      <span>Learn to Sail questions</span>
                    </div>
                    <span className="availability-pill available">{(joinSetup.questions || []).length}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">✅</span>
                      <span>Effective join questions</span>
                    </div>
                    <span className="availability-pill available">{effectiveJoinQuestions.length}</span>
                  </div>
                  <div className="availability-status-item">
                    <div className="availability-status-label">
                      <span className="availability-status-icon">💷</span>
                      <span>Indicative price</span>
                    </div>
                    <span className="availability-pill available">
                      {joinSetup.indicativePriceMajor
                        ? formatMoney(Math.round(Number(joinSetup.indicativePriceMajor || 0) * 100), stripeSetup?.currency || 'GBP')
                        : 'Not set'}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                  <div className="card" style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600 }}>Generic Learn to Sail link</div>
                    <div className="text-muted" style={{ marginTop: 4, wordBreak: 'break-all' }}>
                      {genericJoinUrl || 'Unavailable until the generic waitlist is open or a public course exists.'}
                    </div>
                    {genericJoinUrl ? (
                      <button type="button" className="btn btn--secondary btn--xs" style={{ marginTop: 12 }} onClick={() => handleCopyLink('generic', genericJoinUrl)}>
                        {copiedLinkKey === 'generic' ? 'Copied' : 'Copy link'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="card outings-modal__panel" style={{ padding: 16 }}>
                <div className="outings-modal__panel-head">
                  <div>
                    <h3 style={{ margin: 0 }}>Learn to Sail questions</h3>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      These use the same question shape as the club join form and can sync into member profile fields.
                    </div>
                  </div>
                  <button type="button" className="btn btn--secondary btn--xs" onClick={addJoinQuestion}>
                    Add question
                  </button>
                </div>

                {(joinSetup.questions || []).length > 0 ? (
                  <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                    {(joinSetup.questions || []).map((question) => (
                      <div key={question.id} className="card" style={{ padding: 12 }}>
                        <div className="outings-modal__details-grid">
                          <label style={{ gridColumn: '1 / -1' }}>
                            <span>Question label</span>
                            <input className="input" value={question.label} onChange={(event) => updateJoinQuestion(question.id, { label: event.target.value })} />
                          </label>

                          <label>
                            <span>Type</span>
                            <select className="input" value={question.type} onChange={(event) => updateJoinQuestion(question.id, { type: event.target.value })}>
                              <option value="text">Short text</option>
                              <option value="textarea">Paragraph</option>
                              <option value="number">Number</option>
                              <option value="yesno">Yes / No</option>
                              <option value="date">Date</option>
                              <option value="select">Select</option>
                              <option value="checkbox">Checkbox / Acknowledgement</option>
                            </select>
                          </label>

                          <label>
                            <span>Required</span>
                            <select className="input" value={question.required ? 'required' : 'optional'} onChange={(event) => updateJoinQuestion(question.id, { required: event.target.value === 'required' })} disabled={question.type === 'checkbox'}>
                              <option value="optional">Optional</option>
                              <option value="required">Required</option>
                            </select>
                          </label>

                          <label>
                            <span>Sync to profile</span>
                            <select className="input" value={question.syncToProfile ? 'sync' : 'join_only'} onChange={(event) => updateJoinQuestion(question.id, { syncToProfile: event.target.value === 'sync' })}>
                              <option value="join_only">Join answers only</option>
                              <option value="sync">Join answers + member profile</option>
                            </select>
                          </label>

                          {question.type === 'select' ? (
                            <label style={{ gridColumn: '1 / -1' }}>
                              <span>Options</span>
                              <input className="input" value={(question.options || []).join(', ')} onChange={(event) => updateJoinQuestion(question.id, {
                                options: event.target.value.split(',').map((option) => option.trim()).filter(Boolean),
                              })} placeholder="Option 1, Option 2" />
                            </label>
                          ) : null}
                        </div>

                        <div style={{ marginTop: 12 }}>
                          <button type="button" className="btn btn--secondary btn--xs" onClick={() => removeJoinQuestion(question.id)}>
                            Remove question
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted" style={{ marginTop: 16, marginBottom: 0 }}>
                    No Learn to Sail-specific questions yet. Use the mode above if you only want to reuse the club join form.
                  </p>
                )}
              </section>
            </>
          ) : null}

          <div className="form-actions outings-modal__actions">
            {activePanel === 'courses' && activeCourseId !== '__new__' ? (
              <SubmitButton type="button" label="Delete course" onClick={handleDelete} disabled={saving} variant="danger" />
            ) : <span />}
            <div style={{ display: 'flex', gap: 8 }}>
              <SubmitButton type="button" label="Close" onClick={onClose} disabled={saving} variant="ghost" />
              {activePanel === 'courses' ? (
                <SubmitButton type="button" label={saving ? 'Saving…' : 'Save course'} onClick={() => handleSave()} disabled={loading || saving} variant="primary" />
              ) : null}
              {activePanel === 'joinSetup' ? (
                <SubmitButton type="button" label={saving ? 'Saving…' : 'Save join setup'} onClick={handleSaveJoinSetup} disabled={loading || saving} variant="primary" />
              ) : null}
            </div>
          </div>
          {stripeGuardDialog}
        </div>
      </div>
    </div>
  );
}
